import { isCollaborationReadOnly } from '../../lib/composer/collaborationMode.js';
import { useWorkbenchSurface, useLegacyPanelIntro } from './sessionWorkspace/useWorkbenchSurface.js';
import SessionViewControls from './SessionViewControls.jsx';
import { runWithSupervisor } from '../../lib/runtime/supervisorBridge.js';
import SessionRunRecovery from '../activity/SessionRunRecovery.jsx';
// Apolo IDE — SessionWorkspace (active-session shell).
//
// Layout: breadcrumb header + ExecutionChat + SessionComposer +
// collapsible SidePanel. Loads messages from /api/code/sessions/:id/messages
// on mount and on session change. Posts user turns to the same endpoint —
// real streaming hookup lands in Epic 6.
//
// Header: apenas voltar. Editor/Chat, sessão, painéis e toolbar ficam na aba
// Menu do rail direito (Saídas | Canvas | Menu).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, Library, ListTodo, MessageSquare, PanelRightClose, Route, SlidersHorizontal, Wrench } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { apiFetch, apiUrl, getStoredToken } from '../../lib/api.js';
import { persistMessages } from '../../lib/persistMessages.js';
import { isCompactCommand, buildCompactSummary, compactMarkerMessage } from '../../lib/compactContext.js';
import { autoCompactBeforeSend, compactOnOverflow } from './sessionWorkspace/autoCompactGate.js';
import { parseBoundary, MessagesResponseSchema } from '../../lib/codeSchemas.js';
import { runCloudTurn } from '../../lib/cloudStream.js';
import ExecutionChat from './ExecutionChat.jsx';
import OutputsRail from './chat/OutputsRail.jsx';
import CanvasPanel from './chat/CanvasPanel.jsx';
import ChatRightMenu from './chat/ChatRightMenu.jsx';
// Sprint tasking-cursor-ide P4+ — Cursor-IDE right panel (Tasks/Subagents/Eval DAG).
import TasksPanel from './chat/TasksPanel.jsx';
import SessionFileTabs from './chat/SessionFileTabs.jsx';
import PlanTabView from './chat/PlanTabView.jsx';
import ContextTabView from './chat/ContextTabView.jsx';
import { useFileAttachments } from '../../hooks/useFileAttachments.js';
import LiveActivity from './chat/LiveActivity.jsx';
import PendingMessages from './chat/PendingMessages.jsx';
import LearnedPill from './chat/LearnedPill.jsx';
import { useSessionLearned } from '../../hooks/useSessionLearned.js';
import { getProviderLabel } from '../../lib/providerLogos.js';
import SessionComposer from './SessionComposer.jsx';
import SidePanel from './SidePanel.jsx';
import StreamingMessage from './StreamingMessage.jsx';
import GenericErrorBoundary from '../ui/GenericErrorBoundary.jsx';
import CodeIdePanel from '../code-ide/CodeIdePanel.jsx';
import GitConnectionBar from './GitConnectionBar.jsx';
import ComposerOverflowMenu from './ComposerOverflowMenu.jsx';
import {
  prepareVoiceAwareTurn,
  voiceReadFlags,
  voiceTurnPolicy,
  maybeVoiceSkipSystemPrompt,
  maybeVoiceSkipConstitution,
  voiceComposeSystem,
  voiceRoundTask,
  voiceFilterExtraTools,
  voiceExcludeTools,
  voiceSessionKind,
  voiceCapHistory,
  voiceAllowsTeamTurn,
} from './sessionWorkspace/voiceTurn.js';
import { useSessionShortcuts } from '../../hooks/useSessionShortcuts.js';
import { useStreamingChat } from '../../hooks/useStreamingChat.js';
import { recordOutcome } from '../../hooks/useOutcomes.js';
import { recordTurnTrace } from '../../hooks/useTurnTrace.js';
import { runLocalCraftJudge } from '../../lib/learning/craftJudgeClient.js';
import { useSessionObserver } from '../../hooks/useSessionObserver.js';
import { useKeepRateEval } from '../../hooks/useKeepRateEval.js';
import { surfaceOrphanRunNotice } from './sessionWorkspace/harnessSignalWire.js';
import { useLLMAdapters } from '../../hooks/useLLMAdapters.js';
import { DEFAULT_MAX_ITERATIONS, runLocalAgentLoop, terminalNotice, capConversation } from '../../lib/agentLoop.js';
import { readPermClassifierEnabled } from '../../lib/flags/sprintFlags.js';
import { buildPermissionClassifier } from '../../lib/loop/permissionClassifier.js';
import { buildAutoReviewConfig } from '../../lib/loop/autoReviewConfig.js';
import { buildE6Hooks } from '../../lib/loop/wire/e6Gate.js';
import { asSessionId } from '../../lib/brandedIds.js';
import { agentTurnRequest } from '../../lib/apiContracts.js';
import { defaultToolPolicy } from '../../lib/toolPolicy.js';
import { useSelfRepair } from '../../hooks/useSelfRepair.js';
import { selfRepairLoop } from '../../lib/selfRepair/selfRepairLoop.js';
import { runCheck, detectVerifyCommand } from '../../lib/selfRepair/verifyChecker.js';
// wire-dead-engines E1 — author the missing lint/types/smoke verifiers + the live-preview
// console one, threaded into the AUTO_CORRECT verify gate (default-ON, flag-gated).
import { buildDefaultVerifiers } from '../../lib/oracle/verifierFactories.js';
import { previewConsoleVerifier, runOracle } from '../../lib/oracle/oracle.js';
import { readVerifiersDefaultEnabled, readDagExecutorEnabled, readCheckpointsEnabled, readDagLearningEnabled, readShowSelfImproveEnabled, readFreshVerifierEnabled, readNoSilentGreenEnabled, readRoleScaffoldEnabled, readDagParallelEnabled, readRuleTiersEnabled } from '../../lib/flags/sprintFlags.js';
// role-scaffold-twv — prescribed Thinker→Worker→Verifier over the DAG executor seam. The Verifier stays
// the oracle (verifyTask); the role pool is the (formerly dead) Council roster, now wired to the live path.
import { composeRoleScaffold } from '../../lib/roles/roleScaffold.js';
import { assignRoles } from '../../lib/roles/roleAssignment.js';
import { dagWantsScaffold, roleAssignForTask } from '../../lib/roles/engineDecision.js';
import { COUNCIL_AGENTS } from '../../lib/councilAgents.js';
// arch-fable5 A3 — fresh-context acceptance verifier (pure compose; oracle still judges DONE).
import { makeFreshVerifier } from '../../lib/oracle/freshVerifier.js';
// per-task verifier (M1/M2/A3) extracted from this god-component into a pure, unit-tested factory.
import { buildVerifyTask } from '../../lib/oracle/buildVerifyTask.js';
import { mapDagResultToOutcome } from '../../lib/dagOutcome.js';
// wire-dead-engines E3 — the DAG executor as the real driver on approve (vs the prose turn).
import useTaskExecutor from '../../hooks/useTaskExecutor.js';
import { adaptTasksForExecutor } from '../../lib/tasks/adaptTasks.js';
import { verifiersForTask } from '../../lib/acceptanceVerifier.js';
import { buildSpecFromPlan } from '../../lib/specs/specModel.js';
// wire-dead-engines E4 — live checkpoints (save-on-green / restore-N-1) for the executor.
import { makeGitCheckpoint } from '../../lib/checkpoint/gitCheckpoint.js';
// wire-dead-engines E6 — local agent hooks (Kiro) bound to the executor's task events.
// runMatchedHooks + EXECUTOR_EVENT_TO_HOOK moved to useExecutorEvents (E6-WS1-T02)
import { useTurnRouter } from '../../hooks/useTurnRouter.js';
import { resolveDispatchModel } from '../../lib/loop/resolveDispatchModel.js';
import { resolveLoopOverrides } from '../../lib/turnRouter/applyDecision.js';
import TurnRoleIndicator from './TurnRoleIndicator.jsx';
import { buildAvailableModels } from './sessionWorkspace/codeRouting.js';
import { HEADER_MENU_GROUPS } from './sessionWorkspace/headerMenuConfig.js';
import { useTurnDispatch } from './sessionWorkspace/useTurnDispatch.js';
import { useExecutorEvents } from './sessionWorkspace/useExecutorEvents.js';
import { useStopAgent } from './sessionWorkspace/useStopAgent.js';
import { buildChatFn } from './sessionWorkspace/buildChatFn.js';
import { beginRunLifecycle } from './sessionWorkspace/runLifecycle.js';
import { createTurnEventPipeline } from './sessionWorkspace/turnEventPipeline.js';
import { proposePlanFromOutcome } from './sessionWorkspace/planProposal.js';
import { resolveTurnRoute } from './sessionWorkspace/resolveTurnRoute.js';
import { autoModeContext, tracksTurnDecision, turnDecisionMetadata, turnDecisionVisible, turnSystemMessages, turnIterationLimit } from '../../lib/turnRouter/autoMode.js';
import { runSpecGate } from './sessionWorkspace/sendFlow.js';
import { useQueuedSendDrain } from './sessionWorkspace/useQueuedSendDrain.js';
import { useBlockerActions } from './sessionWorkspace/useBlockerActions.js';
import SelfRepairPanel from './SelfRepairPanel.jsx';
import { maybeAmbientBrain, isAmbientEnabled } from '../../lib/ambientBrain.js';
import { buildAgentHistory, expandContinuePrompt } from '../../lib/agentHistory.js';
import { clearConsumedIfEnabled } from '../../lib/loop/wire/toolResultClearingGate.js';
import { filterSteeringByScope, extractPathsFromText } from '../../lib/steeringScope.js';
import { buildTurnTelemetry } from '../../lib/turnTelemetry.js';
import { buildTurnTraceRecord } from '../../lib/turnTraceRecord.js';
import { buildUserContent } from '../../lib/agentMessages.js';
import { constitutionForSession, buildTaskAnchor, looksLikeComplexTask, modeDirective, currentDateLine } from '../../lib/codeAgentMode.js';
import { sessionKindOf, isChatSession, chatSansFolderOf } from '../../lib/sessionKind.js';
import { decidePlanPropose } from '../../lib/loop/planPropose.js';
import { chatTurnTextWithAttachments } from '../../hooks/useFileAttachments.js';
import { resolveSubagentTools, resolveCrossReviewModel } from '../../lib/subagentTools.js';
import { useToolApproval } from '../../hooks/useToolApproval.jsx';
import { fetchSessionSystemPrompt } from '../../lib/fetchSessionSystemPrompt.js';
import {
  buildRunOutcomePayload,
  setLastOutcomeId,
  setRunAgentMeta,
} from '../../lib/runOutcomeBridge.js';
import { syncExternalSignalFromGit } from '../../lib/finalizeExternalOutcome.js';
import { sessionRoot } from '../../lib/sessionRoot.js';
import { ensureSessionRoot, NO_ROOT_USER_MESSAGE } from '../../lib/ensureSessionRoot.js';
import { useSteering } from '../../hooks/useSteering.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import SteeringIndicator from './SteeringIndicator.jsx';
import { runTeamChatTurn } from './team/teamTurnRunner.js';
import { useSemanticIndexAutoLoad } from '../../hooks/useSemanticIndexAutoLoad.js';
import { useEvaluatorAutoRun } from '../../hooks/useEvaluatorAutoRun.js';
import { useOutputsCollapsed } from '../../hooks/useOutputsCollapsed.js';
import useStickToBottom from '../../hooks/useStickToBottom.js';
import useChatToolbarState from '../../hooks/useChatToolbarState.js';
import {
  autoContinueStatusMessage,
  buildAutoContinuePrompt,
  shouldAutoContinue,
} from '../../lib/agentAutoContinue.js';
import {
  subscribeRun,
  getRunSnapshot,
  setRunMessages,
  setRunning,
  seedRunMessages,
  setRunController,
  getRunController,
  enqueueSend,
  removeQueuedAt,
} from '../../lib/agentRunStore.js';
import TodoDropdown from './TodoDropdown.jsx';
import { usePlanState, dispatchPlan, setPendingPlan, usePendingPlan, getPendingPlan, clearPendingPlan } from '../../lib/planStateStore.js';
import PlanProposalCard from './chat/PlanProposalCard.jsx';
import { buildPlanApprovalText, buildPlanRejectText, EXECUTE_APPROVED_PREFIX } from '../../lib/planContinuation.js';
import { buildRunResultEvent } from '../../lib/loop/runResultEvent.js';
import { taskEventToTodoStatus, extractUserDecisions, dagSummaryLine, dagConcurrencyForRoute } from '../../lib/loop/dagPlanBridge.js';
import { makeDagTaskRunner, taskTimeoutNotice, withDagDeadline, makeThinkerEventSink } from './sessionWorkspace/dagTaskRunner.js';
import { buildPersistedMessages, turnErrorOf } from '../../lib/artifacts.js';
import { resolveExtraTools } from '../../lib/extraTools.js';
import { buildRulesInjection } from '../../lib/agentRulesPrefs.js';
import { codeDoctrineBlock } from '../../lib/loop/chatDoctrineGate.js';
import { buildDagWorkerMessages } from '../../lib/loop/dagWorkerPrompt.js';
import { buildPlanContextDigest } from '../../lib/loop/planContextDigest.js';
import { shouldRunTeamTurn } from '../../lib/loop/enginePrecedence.js';
import { dedupeOptimisticEcho } from '../../lib/loop/dedupeOptimisticEcho.js';
import './chat/redesign.css';

// v10.7: resolve o roteamento do agent loop pra QUALQUER modelo.
//   • LOCAL (Ollama/LM Studio) → { kind:'local', baseUrl, model, isLmStudio }
//     → chamada via IPC direto (window.apolo.ollama/lmstudio.chat)
//   • CLOUD (Claude/GPT/Gemini/DeepSeek/Groq/...) → { kind:'cloud', model, llmAdapterId }
//     → chamada via Worker /agent-turn (que tem as API keys). O loop roda
//       SEMPRE no renderer (tools executam local), só a chamada do LLM difere.
// P5-T02 — detectRoute + buildAvailableModels extracted to sessionWorkspace/codeRouting.js
// (pure, unit-tested). The component imports them; the model→route logic + the registry
// read no longer live in this 2400-LOC shell.

// P5-T02 — the header-menu groups (data + a dozen menu-only icon imports) moved to
// sessionWorkspace/headerMenuConfig.js. The component renders HEADER_MENU_GROUPS.

// Auditoria 2026-07-02 — persiste a mensagem do usuário no DISPATCH (sobrevive a
// reload no meio do turno). Helper module-level (guard fora da função do
// componente pra não inflar o dp-loc-ratchet). Fire-and-forget.
function persistUserMessageEarly(sessionId, text) {
  if (!sessionId || !text) return;
  persistMessages(sessionId, [{ role: 'user', content: text }]).catch(() => {});
}

// S4 (E5) — feed dedicado do Guard-rails panel (helper module-level, mesmo
// racional do persistUserMessageEarly: fora da função medida pelo dp-loc).
function dispatchAutoReview(sessionId, evt) {
  window.dispatchEvent(new CustomEvent('apolo:autoReview', {
    detail: { sessionId, decision: evt.decision, name: evt.name, reason: evt.reason },
  }));
}

// Wave-D — steering path-scoped (04-DOCS-VENDORS: consenso Cursor globs / Claude
// rules paths: / Codex AGENTS.md aninhado). Regra com `paths:` na 1ª linha só
// entra quando o TURNO toca um path que casa — mencionado no texto OU editado
// em runs anteriores da sessão (D-10 v2); sem `paths:` = sempre-ativa
// (byte-idêntico ao comportamento anterior). Module-level: fora do dp-loc.
const sessionEditedPaths = new Map(); // sessionId → últimos paths editados (cap 50)
function noteEditedPaths(sessionId, files) {
  if (!sessionId || !Array.isArray(files) || !files.length) return;
  const cur = sessionEditedPaths.get(sessionId) || [];
  sessionEditedPaths.set(sessionId, [...new Set([...cur, ...files])].slice(-50));
}
function scopedSteeringFor(steeringHook, text, sessionId) {
  const contextPaths = [...extractPathsFromText(text), ...(sessionEditedPaths.get(sessionId) || [])];
  return filterSteeringByScope(steeringHook?.sourcesRef?.current || [], contextPaths, { tiersEnabled: readRuleTiersEnabled() });
}

// Wave-E — sinais do WRAPPER do loop viram learning events; auditoria 07-06
// moveu o handler pra harnessSignalWire.js (testável) e ligou o run_triage
// (Wave-J) que estava dead-wired.

export default function SessionWorkspace({ session, onBack, hideGit = false }) {
  // v12 (#82) — run state (mensagens + running + abort controller) mora no
  // agentRunStore POR SESSÃO, não no componente. Assim a run sobrevive a sair e
  // voltar da tela (unmount/remount): o loop órfão continua escrevendo no store
  // e a view remontada reconecta. Conserta "saio da tela e volto, a LLM para".
  const [runState, setRunState] = useState(() => getRunSnapshot(session?.id));
  useEffect(() => {
    surfaceOrphanRunNotice(session?.id); setRunState(getRunSnapshot(session?.id)); // aviso de run órfão (Wave-J wire) ANTES do snapshot + reconecta
    return subscribeRun(session?.id, setRunState);
  }, [session?.id]);
  const messages = dedupeOptimisticEcho(runState.messages); // E7 achado live: colapsa bolha otimista+persistida da mesma msg
  // (Pedro: "um mock que nao tem pra nada") — the derived FILE ctx-tabs were
  // display-only (not clickable / not real open editors), so they read as a fake
  // mock. Reverted to the honest single session tab + the worktree breadcrumb;
  // real per-file tabs need the in-app editor to accept an external open (own
  // feature). deriveFileTabs/SessionFileTabs stay (tested) for that future wire.
  const localRunning = runState.running;
  // Self-improve por-sessão: o que a sessão ENSINOU vira uma pill discreta no chat
  // (antes só ia pro Discord). Dispensável; reaparece quando aprende algo novo.
  const learned = useSessionLearned(session?.id, localRunning);
  const [learnedDismissedAt, setLearnedDismissedAt] = useState(null);
  // Setters que TODAS as escritas de mensagem/running usam — gravam no store
  // (duráveis através de unmount), não no estado do componente.
  const setMessages = useCallback((u) => setRunMessages(session?.id, u), [session?.id]);

  // Self-Repair Loop (E4) — opt-in (default OFF). Drives the CHECK→FIX→DOUBLECHECK→
  // TROUBLESHOOT→ESCALATE harness post-turn, as a DRIVER over runLocalAgentLoop.
  const selfRepair = useSelfRepair();
  const [repairReport, setRepairReport] = useState(null);
  // wire-dead-engines E3 — the DAG executor bridge (apolo:taskGraph + apolo:checkpoint).
  // Mounted here (was never instantiated by any component); driven on plan-approve.
  const taskExec = useTaskExecutor(session?.id);
  // Turn Role Router (E4) — opt-in (default OFF). Classifies each turn's role and
  // routes it to {model tier, toolset, permission mode} BEFORE the run. OFF ⇒ the
  // decision is null and every wired expression is byte-identical to today.
  const turnRouter = useTurnRouter();
  // Whether the PREVIOUS turn wrote code — a signal that suppresses the 'chat' role
  // (a short "ok, continua" after a write is a build continuation, not small talk).
  const lastTurnTouchedCodeRef = useRef(false);
  const setLocalRunning = useCallback((b) => setRunning(session?.id, b), [session?.id]);
  const observe = useSessionObserver(session?.id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Planning Mode — the session's plan/TODOs (session-keyed store, survives remount).
  const planState = usePlanState(session?.id || '');
  // Plan Mode v2 (E4) — the STRUCTURED pending plan from present_plan, if any. When
  // present it drives the rich PlanProposalCard (summary + per-step tool/success +
  // clarifying questions), which is the PRIMARY approve surface — it supersedes the
  // prose-scraped TodoDropdown approve UI so two competing approve buttons never show.
  const pendingPlan = usePendingPlan(session?.id || '');
  const [todoOpen, setTodoOpen] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState('preview');
  const keepEval = useKeepRateEval(session, panelOpen, panelTab); // E5/D — mede quando Tasks abre
  const [ideMode, setIdeMode] = useWorkbenchSurface();
  // (sprint plan-tab) — a header "Plano" tab opens a focused to-do view
  // (PlanTabView) showing the live plan: done ✓ / executing → / pending ○. The
  // tab appears once a plan is montado (todos exist) and auto-opens the first
  // time that happens. planTabOpen drives BOTH the active chip and the content.
  const [planTabOpen, setPlanTabOpen] = useState(false);
  const planTodos = planState?.todos || [];
  const planCount = planTodos.length;
  const planDone = planTodos.filter((t) => t?.status === 'done').length;
  const planPhase = planState?.phase;
  // A aba "Plano" é a tela de EXECUÇÃO (o que rodou / está rodando agora). Só
  // existe quando o plano foi aprovado e está executando — ou terminou. Em
  // 'planning'/'awaiting_approval' quem governa o feed é o PlanProposalCard
  // (aprovação), então a aba não rouba a tela dele.
  const planActionable = planCount > 0 && (planPhase === 'executing' || planPhase === 'done');
  const prevPlanPhaseRef = useRef(planPhase);
  useEffect(() => {
    // entrou em execução = plano montado e rodando → abre a aba nova (1x, na
    // transição; um fechamento manual depois é respeitado).
    if (prevPlanPhaseRef.current !== 'executing' && planPhase === 'executing') {
      setPlanTabOpen(true);
    }
    // sem plano executável (reset/rejeição) → fecha pra não deixar aba órfã.
    if (!planActionable) setPlanTabOpen(false);
    prevPlanPhaseRef.current = planPhase;
  }, [planPhase, planActionable]);
  // (sprint context-tab) — aba "Contexto" aberta pelo "Add context" do composer.
  // Layout estilo wiki (sidebar + preview), aceita QUALQUER arquivo. O store de
  // arquivos vive AQUI (lifted) pra sobreviver à troca de aba e alimentar o
  // badge. Texto/markdown viram conhecimento permanente (code_user_memories); o
  // composer reusado abaixo segue alimentando o chat principal.
  const [contextTabOpen, setContextTabOpen] = useState(false);
  // Limites largos: a aba Contexto recebe vídeos/arquivos grandes (mantidos como
  // File ref + object-URL, sem ler em memória) — daí 250 MB e até 30 itens.
  const contextFiles = useFileAttachments({ maxFileSize: 250 * 1024 * 1024, maxFiles: 30 });
  const contextCount = contextFiles.attachments.length;
  // O chip "Contexto" aparece quando a aba está aberta OU há arquivos staged.
  const contextAvailable = !ideMode && (contextTabOpen || contextCount > 0);
  // E8 — painel direito: 'saidas' | 'canvas' | 'menu' (work-surface + menu do chat).
  const [rightView, setRightView] = useState(() => {
    try {
      const v = localStorage.getItem('apolo:rightView');
      // Deliberate non-default picks are honored; 'saidas' (the old auto-default)
      // and unset now land on the Cursor Tasks panel (DAG · Specs · Merge Queue).
      if (v === 'canvas') return 'canvas';
      if (v === 'menu') return 'menu';
      return 'tasks';
    } catch {
      return 'tasks';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('apolo:rightView', rightView); } catch { /* noop */ }
  }, [rightView]);
  // Auto-reveal the Tasks panel the first time a run produces a task DAG, so the
  // user sees the oracle-gated graph without hunting for the toggle. Only flips
  // ONCE per "DAG appeared" edge (tracked via a ref) — never fights a manual pick.
  const codeTaskCount = useAgentStore((s) => s.code.tasks?.tasks?.length || 0);
  const sawDagRef = useRef(false);
  useEffect(() => {
    if (codeTaskCount > 0 && !sawDagRef.current) {
      sawDagRef.current = true;
      setRightView('tasks');
    }
    if (codeTaskCount === 0) sawDagRef.current = false;
  }, [codeTaskCount]);
  // E1-T01 — colapso do rail direito (Saídas/Canvas). Colapsado = alça fina; o
  // chat reclama a largura. Persistido em apolo:outputs:collapsed.
  const [outputsCollapsed, setOutputsCollapsed, toggleOutputs] = useOutputsCollapsed();
  useLegacyPanelIntro(setOutputsCollapsed, setRightView);
  // Botão "Design" (painel de preview) — movido do CodeSidebar pro header do chat.
  const previewPanelOpen = useAgentStore((s) => s.code.ui?.previewPanelOpen);
  const togglePreviewPanel = useAgentStore((s) => s.togglePreviewPanel);
  // Largura responsiva da coluna do chat (feed + composer). min(rem, %) → preenche
  // o espaço lateral em vez dos 46rem fixos que deixavam margens enormes; com a
  // sidebar esquerda COLAPSADA o cap sobe (o chat reclama o espaço liberado).
  const sidebarCollapsed = useAgentStore((s) => s.code.ui?.sidebarCollapsed);
  const chatColMaxWidth = sidebarCollapsed ? 'min(96rem, 96%)' : 'min(76rem, 94%)';
  const toolbar = useChatToolbarState();
  // Ref pro setter — o handler de apolo:openDiff (dep array vazio) re-expande o
  // rail sem recriar o listener a cada mudança de estado.
  const setOutputsCollapsedRef = useRef(setOutputsCollapsed);
  setOutputsCollapsedRef.current = setOutputsCollapsed;

  // E2/E3/E8 — Revisar/Abrir num artefato dispara apolo:openDiff → mostra o
  // Canvas (que foca a aba Diff sozinho). Desktop: coluna direita; no mobile o
  // rail não aparece, então segue pela tab bar.
  useEffect(() => {
    function onOpenDiff() {
      setRightView('canvas');
      // Abrir um artefato precisa do rail visível — se estiver colapsado,
      // re-expande (a feature está a 1 evento de distância, nunca some).
      setOutputsCollapsedRef.current?.(false);
    }
    window.addEventListener('apolo:openDiff', onOpenDiff);
    return () => window.removeEventListener('apolo:openDiff', onOpenDiff);
  }, []);

  // Clicar na LearnedPill abre a aba Evaluator (regras aprendidas).
  useEffect(() => {
    const onOpenLearned = () => { setPanelTab('learned-rules'); setPanelOpen(true); };
    window.addEventListener('apolo:openLearnedRules', onOpenLearned);
    return () => window.removeEventListener('apolo:openLearnedRules', onOpenLearned);
  }, []);

  // Clicar num path de arquivo dentro de uma mensagem do agente (`hero.html` etc.)
  // dispara apolo:openFile → abre no app/editor do SO, resolvido na raiz da sessão
  // (o handler confina em resolveInsideRoot → no-op seguro se o path não existir).
  // Pedro (2026-07-09): "cadê o link pra abrir o hero".
  useEffect(() => {
    const onOpenFile = (e) => {
      const filePath = e?.detail?.path;
      const root = session?.worktree_path || session?.directory;
      if (!filePath || !root) return;
      window.apolo?.file?.openPath?.({ root, filePath });
    };
    window.addEventListener('apolo:openFile', onOpenFile);
    return () => window.removeEventListener('apolo:openFile', onOpenFile);
  }, [session?.worktree_path, session?.directory]);

  const refreshSessions = useAgentStore((s) => s.refreshSessions);
  const updateCodeSession = useAgentStore((s) => s.updateCodeSession);
  const allSessions = useAgentStore((s) => s.code.sessions);
  const { adapters, loading: adaptersLoading } = useLLMAdapters({ kind: 'text' });

  const fallbackRoots = useMemo(
    () => (Array.isArray(allSessions)
      ? allSessions
        .map((s) => sessionRoot(s))
        .filter((p) => p.startsWith('/'))
      : []),
    [allSessions],
  );

  // ZeroEntropy — background index so semantic_search has vectors ready.
  useSemanticIndexAutoLoad(session?.id, sessionRoot(session));

  // Self-driving self-improvement: run the Ollama evaluator on the daily cadence
  // and auto-apply gated learned rules to .apolorules (Tier-S spine).
  useEvaluatorAutoRun(sessionRoot(session), session?.id);

  // E2b — Project steering: load .apollo/steering/*.md once per session root.
  // Web (no Electron): window.apolo?.steering?.load absent → sources = [] (safe).
  const steering = useSteering(sessionRoot(session));

  // S4 (E4) — Auto-review policy: load .apollo/permissions.json once per root.
  // Absent → { enabled:false } (auto-review off, byte-identical to pre-S4).
  const permissions = usePermissions(sessionRoot(session));


  // Bind repo root as soon as the session opens — don't wait for the first agent run.
  useEffect(() => {
    if (!session?.id) return;
    const root = sessionRoot(session);
    if (root.startsWith('/')) return;
    let cancelled = false;
    (async () => {
      const bound = await ensureSessionRoot(session, {
        updateSession: async (id, patch) => {
          const res = await updateCodeSession(id, patch);
          if (res.ok) refreshSessions();
          return res;
        },
        fallbackRoots,
      });
      if (cancelled || !bound.startsWith('/')) return;
      refreshSessions();
    })();
    return () => { cancelled = true; };
    // `session` is read whole inside, but we intentionally key only on the
    // identity fields that affect the bind — depending on the object itself
    // would re-run on every unrelated session mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.directory, session?.worktree_path, updateCodeSession, refreshSessions, fallbackRoots]);

  // Brain-lazy-retrieval (E3) — warm-up. Fire ONCE per session open: prime the
  // server-side fused-context cache in the BACKGROUND so the first questions hit
  // a warm cache instead of paying the synchronous RAG round-trips. Fire-and-forget
  // (the endpoint 202s immediately and no-ops on an empty query); never blocks UI.
  const warmedForRef = useRef(null);
  useEffect(() => {
    const id = session?.id;
    if (!id || warmedForRef.current === id) return;
    // Prefer the most recent user message (continuing a conversation), else the
    // session's title/objective (a fresh session). Empty → the endpoint no-ops.
    const snap = getRunSnapshot(id);
    const lastUser = Array.isArray(snap?.messages)
      ? [...snap.messages].reverse().find((m) => m?.role === 'user')
      : null;
    const fromMsg = typeof lastUser?.content === 'string' ? lastUser.content : '';
    const fromMeta = [session?.title, session?.name, session?.objective]
      .find((s) => typeof s === 'string' && s.trim()) || '';
    const query = (fromMsg || fromMeta).slice(0, 2000).trim();
    if (!query) return;
    warmedForRef.current = id;
    const controller = new AbortController();
    apiFetch(`/api/code-ide/sessions/${id}/warm-context`, {
      method: 'POST',
      body: { query },
      signal: controller.signal,
    }).catch(() => {});
    return () => controller.abort();
    // Key only on session id — the ref makes it at-most-once per session, and
    // re-running on unrelated session mutations would re-warm needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Estado vivo do turno (fase: pensando|gerando|tool) pro indicador LiveActivity.
  // Component-local (efêmero): no remount, localRunning vem do store e o
  // LiveActivity cai pra "Pensando…" até o próximo delta — sem perda real.
  const [liveStatus, setLiveStatus] = useState(null);
  // Modo Equipe (sprint subagents-team): prefs do toggle/settings do composer.
  const teamModeOn = useAgentStore((s) => !!s.code.ui.team_mode);
  const teamCap = useAgentStore((s) => s.code.ui.team_cap);
  const teamWorkerModel = useAgentStore((s) => s.code.ui.team_worker_model);
  const teamReadOnly = useAgentStore((s) => !!s.code.ui.team_read_only);
  // S3 E3b — Spec Gate (opt-in, default OFF). Absent flag = falsy = byte-identical today.
  const specModeOn = useAgentStore((s) => !!s.code.ui.spec_mode);
  // E6-WS1-T01 — dispatch wiring (spec refs, event listeners, session-switch cleanup,
  // auto-first-message) extracted to useTurnDispatch. runAgentRef reassigned every render
  // so the resume always invokes the latest closure (current session + setMessages).
  const {
    pendingSpecTurnRef, setPendingSpecTurn, generatingSpec, setGeneratingSpec, specGenAbortRef,
    runAgentRef, sessionIdRef, handleSendRef,
  } = useTurnDispatch({ sessionId: session?.id, adaptersLoading });
  runAgentRef.current = runAgent;

  const loadMessages = useCallback(async () => {
    if (!session?.id) return;
    // E3-WS1-T02 — skip the full-history GET on remount while a run is in flight AND
    // the store already holds this session's messages. seedRunMessages is a no-op
    // while running, so the fetch result would be discarded anyway; the live bubbles
    // already survive remount in agentRunStore (P3) → 0 wasted history round-trips on
    // tab-switch-back mid-run. (If running but the store is empty — a rare background
    // run never opened here — we still fetch so prior history isn't lost.)
    const snap = getRunSnapshot(session.id);
    if (snap?.running && (snap.messages?.length || 0) > 0) return;
    setLoading(true);
    const res = await apiFetch(`/api/code/sessions/${session.id}/messages?limit=200`); // E5-WS3 — só as 200 mais recentes
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'Falha ao carregar mensagens');
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: `Falha ao carregar mensagens: ${res.error || 'erro desconhecido'}` },
      }));
      return;
    }
    setError(null);
    // P2-T03 (code-tab-lift) — parse the response shape AT the boundary (warn mode:
    // logs a labeled warning on a malformed payload and returns it untouched, so live
    // valid traffic is never broken). Catches a changed/garbage shape here instead of
    // as an `undefined.map` deep in ConversationFeed.
    const parsed = parseBoundary(MessagesResponseSchema, res.data, { label: 'code.messages' });
    // seedRunMessages é no-op se a sessão está rodando — assim remontar (voltar
    // pra tela) NÃO apaga as bubbles ao vivo da run com o histórico do banco.
    seedRunMessages(session.id, parsed?.messages || []);
  }, [session?.id]);

  useEffect(() => {
    setError(null);
    loadMessages();
  }, [loadMessages]);

  // Recarrega o histórico quando algum botão de mensagem (rewind/fork) altera o log
  // server-side. Só dispara se o evento pertence a esta sessão. Best-effort.
  useEffect(() => {
    function onMessagesChanged(evt) {
      const sid = evt?.detail?.sessionId;
      if (!sid || sid !== session?.id) return;
      loadMessages();
    }
    window.addEventListener('apolo:messages-changed', onMessagesChanged);
    return () => window.removeEventListener('apolo:messages-changed', onMessagesChanged);
  }, [session?.id, loadMessages]);

  const togglePanel = useCallback(() => setPanelOpen((v) => !v), []);
  const openTab = useCallback((id) => {
    setPanelTab(id);
    setPanelOpen(true);
  }, []);
  // Clicking a tab that's already active closes the panel — feels natural
  // and lets the same button act as toggle+switcher.
  const handleTabClick = useCallback(
    (id) => {
      if (panelOpen && panelTab === id) {
        setPanelOpen(false);
      } else {
        openTab(id);
      }
    },
    [panelOpen, panelTab, openTab],
  );
  useSessionShortcuts({ togglePanel, openTab });

  // useStreamingChat mantido só pelos indicadores de UI (streaming/abort).
  // O dispatch real agora é sempre o runAgent (agent loop, local OU cloud).
  const { streaming, streamingMessage, error: streamError, abort } = useStreamingChat(
    session?.id,
    { onComplete: () => loadMessages() },
  );
  const { requestApproval: requestToolApproval, approvalUi } = useToolApproval(); // E6-WS1-T02

  // Stick-to-bottom for the WHOLE conversation column. The live StreamingMessage
  // renders as a SIBLING of ExecutionChat in this outer scroller, so the follow
  // logic must live HERE (not inside ExecutionChat) to track the streaming answer.
  // Pressing "Ir pro fim" re-engages follow so the view keeps tracking the latest
  // line as tokens arrive; scrolling up disengages without being yanked back.
  const chatScrollRef = useRef(null);
  const chatContentRef = useRef(null);
  const { showJump, onScroll: onChatScroll, scrollToBottom: jumpChatToBottom } = useStickToBottom(
    chatScrollRef,
    chatContentRef,
    session?.id,
  );

  async function handleSend({ text, model, permissions_mode, attachments, plan_mode, dagTasks, executeApproved, initialMessageId }) {
    text = chatTurnTextWithAttachments(isChatSession(session), text, attachments); // E7-T01 — todo turno do chat
    // /compact (DeerFlow) — comando LOCAL, sem turno/custo: marker entra no feed e o
    // histórico modelo-facing reseta (buildAgentHistory); o chat visível fica intacto.
    if (isCompactCommand(text)) {
      const marker = compactMarkerMessage(buildCompactSummary(getRunSnapshot(session.id).messages));
      setMessages((m) => [...m, marker]);
      persistMessages(session.id, [marker]).catch(() => {});
      return;
    }
    // Bug 07-08: turno aprovado desliga o chip Plan (ui.plan_mode grudava → o próximo turno re-planejava).
    // guard PR #618 — idem pro modo 'plan' persistido do chat-home (chatModes).
    if (executeApproved) { const st = useAgentStore.getState(); st.setUiPref('plan_mode', false); if (st.code.chatModes[session.id] === 'plan') st.setChatMode(session.id, null); }
    // I1 — re-submit while a spec is pending: SUPERSEDE the stale turn before
    // anything else. Mark its artifact resolved='superseded' (so the old card can
    // no longer resume the old turn) and null the ref, so only THIS submit runs.
    const stalePending = pendingSpecTurnRef.current;
    if (stalePending && stalePending.sessionId === session?.id) {
      setMessages((m) =>
        m.map((msg) =>
          msg.tool_name === 'spec_review' &&
          msg.metadata?.turnToken === stalePending.turnToken &&
          !msg.metadata?.resolved
            ? { ...msg, metadata: { ...msg.metadata, resolved: 'superseded' } }
            : msg,
        ),
      );
      setPendingSpecTurn(null); // E9-T01 — sincroniza ref+storage
    }

    // Plan Mode (paridade Claude Code): se há um plano proposto na tela e o
    // usuário ESCREVE no chat — em vez de clicar Aprovar/Rejeitar — tratamos a
    // mensagem como feedback e RE-PLANEJAMOS com ela (plan mode permanece
    // ligado). Assim dá pra refinar o plano só digitando, igual ao Claude Code.
    // A bolha mostra o texto CRU; o agente recebe o texto embrulhado como
    // feedback (ver a chamada de runAgent no fim desta função).
    const refiningPlan = !!(getPendingPlan(session?.id) && String(text || '').trim());
    if (refiningPlan) {
      clearPendingPlan(session?.id);
      dispatchPlan(session?.id, { type: 'reject' });
    }
    // Optimistic user bubble so the chat feels live before the stream starts.
    // Shows the user's ORIGINAL text (not the plan-first directive we prepend below).
    const optimistic = {
      id: initialMessageId || `optimistic-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => m.some((item) => item.id === optimistic.id) ? m : [...m, optimistic]);
    // Auditoria 2026-07-02 — persiste a mensagem do usuário AGORA (não só no fim
    // do turno). Antes, um reload/crash no meio de um turno longo apagava a
    // pergunta (só existia na bolha otimista em memória). Fire-and-forget; os
    // sites de fim de turno passam includeUser:false pra não duplicar a linha.
    if (!initialMessageId) persistUserMessageEarly(session?.id, text);
    refreshSessions();

    // Plan Mode v2 (composer toggle): a REAL mode, not a prose prefix.
    // - The directive is injected as a hidden SYSTEM message inside runAgent (NOT
    //   glued to the user text), so it never leaks into the bubble / persisted turn.
    // - Enforcement is HARD: planPropose ⇒ readOnly in the loop (write/edit/run are
    //   denied at the gate regardless of the model), wired in runAgent below.
    // The proposed plan is parsed post-run into the session PlanState → Approve UI.

    // v10.7: agent loop roda no renderer pra TODAS as LLMs (tools executam
    // local via IPC). LOCAL → chamada direta IPC; CLOUD → chamada via Worker
    // /agent-turn (que tem as keys). Assim qualquer modelo "abre o terminal".
    // E1-WS2-T02 — model-pick (pin → session → profile) + Turn Role Router swap +
    // detectRoute num módulo deep (resolveTurnRoute). Devolve o triplo que o resto do
    // handleSend consome (spec gate / runAgent / pendingSpecTurn stash).
    const { effModel, routeDecision, route } = resolveTurnRoute({
      text,
      model,
      sessionModel: session.model,
      adapters,
      effort: useAgentStore.getState().code.ui?.effort,
      turnRouterEnabled: turnRouter.enabled,
      ...autoModeContext(useAgentStore.getState(), session, attachments),
      priorTurnTouchedCode: lastTurnTouchedCodeRef.current,
      observe,
    });

    // S3 E3b — Spec Gate phase-1 (opt-in, default OFF). The whole mechanism (spec
    // generation + timeout + local/cloud probe + skip-safe guards + spec_review card +
    // pendingSpecTurn stash) lives in the sendFlow seam (runSpecGate). handled ⇒ the
    // turn was stashed pending Accept, or the user switched sessions mid-generation →
    // return; !handled ⇒ spec null/aborted/same session → fall through to runAgent.
    if (specModeOn && !pendingSpecTurnRef.current) {
      const handled = await runSpecGate(
        { text, route, effModel, permissionsMode: permissions_mode, attachments, routeDecision },
        {
          session, sessionIdRef, specGenAbortRef,
          setGeneratingSpec, setLiveStatus, setMessages, setPendingSpecTurn,
          scopedSteering: (t) => scopedSteeringFor(steering, t, session.id), apiFetch,
        },
      );
      if (handled) return;
    }

    await runAgent({
      text: refiningPlan ? buildPlanRejectText(text) : text,
      route,
      permissionsMode: permissions_mode,
      attachments,
      routeDecision,
      // Decisão PURA em loop/planPropose.js (histórico #332/#377/F13-E7-T07 preservado).
      // guard PR #618 — chatModes 'plan' (chip Plan da HOME) agora liga o hard gate.
      planPropose: decidePlanPropose({ refiningPlan, planMode: plan_mode, chatMode: useAgentStore.getState().code.chatModes[session.id], isChat: isChatSession(session), executeApproved, text }, looksLikeComplexTask),
      dagTasks, // wire-dead-engines E3 — the approved DAG (null on a normal turn)
      effModel,
    });
  }

  // Roda o agent loop pra QUALQUER modelo (local IPC ou cloud Worker).
  // Monta histórico, executa tools via IPC, empurra bubbles conforme eventos.
  // S3 E3b: accepts optional `spec` (InlineSpec) from the Spec Gate accept flow.
  async function runAgent({ text, route, permissionsMode, attachments, routeDecision = null, spec = null, planPropose = false, dagTasks = null, effModel: agentModel = null }) {
    // Cooperative cancel: Stop/ESC abortam este controller; agentLoop.js checa
    // signal.aborted no topo de cada iteração e antes de cada tool.
    const controller = new AbortController();
    // WS1-T02 — run CONTROL PLANE num módulo deep (runLifecycle): begin liga o
    // spinner + "pensando" + registra o controller; run.armIdleWatchdog (dentro do
    // try, depois do pushMsg) arma o watchdog IDLE; run.onActivity pinga; run.settle
    // (no finally) cancela + reseta com o same-controller guard.
    const run = beginRunLifecycle({
      sessionId: session.id, controller,
      setRunning: setLocalRunning, setLiveStatus, setRunController, getRunController, setRunMessages,
    });
    // Transparency: surface the routed role + model on the indicator (like Cursor's
    // mode). A 'none'/absent decision clears it. Only the router toggle drives this.
    if (tracksTurnDecision(turnRouter.enabled, routeDecision)) {
      turnRouter.setLastDecision(
        turnDecisionMetadata(routeDecision, route),
      );
    }
    setError(null);
    const turnStartedAt = Date.now(); // E2 — telemetria de turno (duração)
    let turnToolNames = []; // E4 — bounded tool-call names for the TurnTrace record (só no caminho principal; DAG/team = [])
    let pipeline = null; // Event/Stream Pipeline (criado no try, DEPOIS do pushMsg); o finally lê pipeline?.toolRecords
    let runOk = false; // Planning Mode — sinal OBJETIVO de conclusão do plano (run-end)
    let agentResult = null;
    try {
      let root = sessionRoot(session);
      const chatSansFolder = chatSansFolderOf(session, root); // E7-T09 — chat sem pasta roda sem root
      if (!root.startsWith('/') && !chatSansFolder) {
        root = await ensureSessionRoot(session, {
          updateSession: async (id, patch) => {
            const res = await updateCodeSession(id, patch);
            if (res.ok) refreshSessions();
            return res;
          },
          fallbackRoots,
        });
      }
      if (!root.startsWith('/') && !chatSansFolder) {
        // Auto-recovery: open folder picker so the user can bind the repo inline
        // instead of having to leave the chat and configure manually.
        if (typeof window !== 'undefined' && window.apolo?.repo?.pickFolder) {
          try {
            const picked = await window.apolo.repo.pickFolder();
            if (picked?.ok && picked.path?.startsWith('/')) {
              try { localStorage.setItem('apolo:lastRepoRoot', picked.path); } catch { /* noop */ }
              const res = await updateCodeSession(session.id, { directory: picked.path });
              if (res?.ok) { refreshSessions(); root = picked.path; }
              else { root = picked.path; } // use it locally even if persist fails
            }
          } catch { /* picker error — fall through to error block */ }
        }
      }
      if (!root.startsWith('/') && !chatSansFolder) {
        setMessages((m) => [
          ...m,
          {
            id: `local-noroot-${Date.now()}`,
            role: 'system',
            content: NO_ROOT_USER_MESSAGE,
            created_at: new Date().toISOString(),
          },
        ]);
        window.dispatchEvent(new CustomEvent('apolo:toast', {
          detail: {
            kind: 'error',
            message: 'Sessão sem pasta do projeto — vincule ~/Apolo antes de usar o agente.',
          },
        }));
        setError('Sessão sem worktree/diretório local. O agente precisa de uma pasta do projeto.');
        return;
      }
      // S3 F1 — precedence parity: LOCAL threads steering+spec into POST /system-prompt
      // (rules>session>STEERING>SPEC>brain>persona>memory>fused). Voz: skip (latência).
      const vtEarly = voiceTurnPolicy(planPropose, DEFAULT_MAX_ITERATIONS);
      const serverPrompt = route.kind === 'local'
        ? await maybeVoiceSkipSystemPrompt(vtEarly, () => fetchSessionSystemPrompt(session.id, {
            steering: scopedSteeringFor(steering, text, session.id),
            spec: spec ?? null,
          }))
        : '';
      const originalTask = expandContinuePrompt(text, messages || []);
      // F13-E7-T05 — kind-aware: chat local recebia a LEI do Code ("máx 3 frases").
      const constitution = maybeVoiceSkipConstitution(vtEarly, () =>
        constitutionForSession(session, { root, model: agentModel || route?.model }));
      // Cloud: agent-turn injects brain + constitution server-side — avoid duplicate system blocks.
      // LOCAL: steering+spec are now part of serverPrompt (assembled at correct
      // precedence server-side), so only the constitution is appended client-side.
      const sys = route.kind === 'local'
        ? [currentDateLine(), serverPrompt, constitution].filter(Boolean).join('\n\n---\n\n') // E14-T06
        : '';

      // chatFn: LOCAL → IPC direto (Ollama/LM Studio). CLOUD → Worker /agent-turn
      // (que resolve adapter+key e devolve tool_calls normalizados).
      const chatFn = buildChatFn(route, session.id, { sourcesRef: { current: scopedSteeringFor(steering, text, session.id) } }, spec); // Wave-D — cloud recebe steering já escopado por paths:

      // E5 (S3 Glass Box) — emit audit event ONCE per turn, at the point steering+spec
      // are finalized. EPHEMERAL + audit-only: never fed back into any prompt.
      // Payload is kept small (well under the 16KB session_events cap): only file
      // metadata (kind/path) + spec summary (design truncated to 500 chars).
      // Skip plain turns where there is nothing to record.
      {
        const steeringFiles = scopedSteeringFor(steering, text, session.id).map((s) => ({
          kind: s.kind,
          path: s.path,
        }));
        if (steeringFiles.length > 0 || spec) {
          observe('steering_spec', {
            steeringFiles,
            specPresent: !!spec,
            spec: spec
              ? {
                  requirements: spec.requirements,
                  acceptance: spec.acceptance,
                  design: String(spec.design || '').slice(0, 500),
                }
              : null,
          });
        }
      }

      // pushMsg/updateMsg — feed writers do turno (usados pelo turnEventPipeline E
      // pelos caminhos team/DAG/ambient/persist/self-repair). Todo o estado de feed do
      // turno (tool cards, streaming bubble, verification seal, finalReasoning) mora no
      // turnEventPipeline; o runAgent lê o que precisa depois do loop pelos getters.
      function pushMsg(msg) {
        const id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setMessages((m) => [...m, { id, created_at: new Date().toISOString(), ...msg }]);
        return id;
      }
      function updateMsg(id, patch) {
        setMessages((m) => m.map((x) => (x.id === id ? { ...x, ...patch } : x)));
      }
      let autoRound = 0;
      // WS-B (incidente tastedesign) — arma o watchdog IDLE do turno DEPOIS do pushMsg
      // (a notícia usa pushMsg). A limpeza (MESMA do finally, pois um await travado
      // nunca chega lá) + o same-controller guard moram no runLifecycle; aqui só a
      // notícia no feed.
      run.armIdleWatchdog({ dagTasks,
        onIdleNotice: (idleMs) => pushMsg({
          role: 'system',
          content: `O turno ficou ${Math.round(idleMs / 60000)} min sem atividade e foi encerrado (provável travamento). Nada do que já apareceu foi perdido — rode de novo se precisar continuar.`,
        }),
      });
      // E1-WS1-T03 — Event/Stream Pipeline num módulo DEEP: pipeline.feed é o antigo
      // onEvent (streaming bubble + tool cards + verification seal + loop_stop/
      // auto_review/loop_step + erro/truncamento). Owns o estado de feed do turno;
      // o runAgent lê tool records / finalReasoning / verification seal pelos getters.
      pipeline = createTurnEventPipeline({
        route, session, pushMsg, updateMsg, setLiveStatus, setError,
        observe, noteEditedPaths, dispatchAutoReview, setRunAgentMeta, keepEval,
        onActivity: run.onActivity, isAborted: () => controller.signal.aborted,
      });
      const onEvent = pipeline.feed;

      // ——— Modo Equipe (sprint subagents-team): toggle ON ⇒ o turno vira
      // plano (líder) → operários em paralelo (driver Hermes) → síntese.
      // 'solo' do líder ⇒ cai SEM ruído pro fluxo normal logo abaixo. A
      // bolinha verde (setLocalRunning via agentRunStore) e o Stop
      // (controller.signal) são os MESMOS do caminho normal.
      if (voiceAllowsTeamTurn(vtEarly) && shouldRunTeamTurn(teamModeOn, { dagTasks, planPropose })) { // voz off; E5-T02 DAG/plano vencem toggle
        const team = await runTeamChatTurn({
          text: originalTask,
          root,
          route,
          cap: Number.isInteger(Number(teamCap)) && Number(teamCap) > 0 ? Number(teamCap) : undefined,
          workerModel: teamWorkerModel || null,
          readOnly: teamReadOnly,
          signal: controller.signal,
          pushMsg,
          updateMsg,
          setLiveStatus,
        });
        if (team.handled) {
          if (team.ok && team.finalText) {
            pushMsg({ role: 'agent', content: team.finalText, model: route.model });
          } else if (!team.ok && team.error) {
            pushMsg({ role: 'system', content: `⚠️ Equipe falhou: ${team.error}` });
          }
          agentResult = { ok: team.ok, finalText: team.finalText || '', stopReason: team.stopReason };
          runOk = !!team.ok; // E6-WS1-T03 — o `return` abaixo pula o runOk=true final, mas o finally usa runOk (planRunHonestlyDone); sem isto uma equipe que ENTREGOU virava plano NÃO-done
          // Persistência C3: user (já persistido no dispatch) → card da equipe
          // (tool team_plan) → síntese. includeUser:false pra não duplicar o user.
          try {
            const bodies = buildPersistedMessages({ userText: text, includeUser: false, finalText: agentResult.finalText });
            const teamCard = team.card
              ? [{ role: 'tool', tool_name: 'team_plan', content: 'Plano da equipe', metadata: team.card }]
              : [];
            const ordered = [...teamCard, ...bodies].filter(Boolean);
            await persistMessages(session.id, ordered);
          } catch (e) {
            console.warn('[SessionWorkspace] team-turn message persistence failed to build', e);
          }
          // Sprint v11 E15 — hands-free TTS playback (team path). Mesmo
          // evento do caminho principal — o hook ignora quando está off.
          try {
            if (team.ok && team.finalText && team.finalText.trim()) {
              window.dispatchEvent(new CustomEvent('apolo:handsFreeReply', {
                detail: { text: team.finalText },
              }));
            }
          } catch { /* best-effort */ }
          refreshSessions();
          return;
        }
        // Líder decidiu 'solo' — segue o agent loop normal abaixo.
      }

      // sprint apolo-brain-vivo (AMB1) + voz: prepareVoiceAwareTurn decide vault/verifiers.
      const { vt, ambientBlock, autoCorrectCfg } = await prepareVoiceAwareTurn({
        planPropose,
        defaultMaxIterations: DEFAULT_MAX_ITERATIONS,
        task: originalTask,
        sessionId: session.id,
        maybeAmbientBrain,
        isAmbientEnabled,
        buildAutoCorrect: async () => {
          // === AUTO_CORRECT verifiers default-ON (wire-dead-engines E1-WS2-T01) ===
          if (!readVerifiersDefaultEnabled()) return null;
          let scripts = null;
          try {
            const res = await window.apolo?.file?.read?.({ root, filePath: 'package.json' });
            scripts = res?.content ? (JSON.parse(res.content).scripts || null) : null;
          } catch { /* no/invalid package.json → no script verifiers (honest no-op) */ }
          const runCommand = (p) => window.apolo.shell.exec({ ...p, cwd: p.cwd || root });
          const previewConsole = (typeof window !== 'undefined' && window.apolo?.preview?.console)
            ? previewConsoleVerifier(window.apolo.preview)
            : null;
          // P3-T03 (code-tab-lift) — wire acceptance verification onto the NORMAL Code turn.
          const verifiers = (spec && spec.acceptance)
            ? verifiersForTask({ task: { acceptance: spec.acceptance }, root, runCommand, scripts, previewConsole, buildDefaultVerifiers })
            : buildDefaultVerifiers({ root, runCommand, scripts, previewConsole });
          return Object.keys(verifiers).length > 0 ? { verifiers, triage: null } : null;
        },
      });
      if (ambientBlock) {
        pushMsg({ role: 'system', content: '🧠 contexto do vault aplicado a este turno' });
      }

      // E8-T07 (D-WIRE) — resolve the flag-gated harvest tools (memory, session_search)
      // ONCE per send, before any runLocalAgentLoop branch (DAG executor, main turn,
      // self-repair) so all of them can offer them. resolveHarvestTools fetches the
      // /descriptor enabled-state (memoized per page-load) and returns only the enabled
      // tools ⇒ flags OFF ⇒ [] ⇒ byte-identical tool set. Fail-soft ([] on error).
      const harvestExtraTools = vtEarly.conversational ? [] : await resolveExtraTools(); // voz: zero extras; WSX web
      const mainExtraTools = voiceFilterExtraTools(vtEarly, [...harvestExtraTools, ...resolveSubagentTools({ chatFn, root, model: agentModel || route.model, reviewModel: resolveCrossReviewModel(agentModel || route.model, buildAvailableModels(adapters)), runLoop: runLocalAgentLoop, sessionId: asSessionId(session.id), signal: controller.signal })]); // E13; voz zera

      // === E3: DAG executor as the REAL driver (wire-dead-engines E3-WS2/WS3) ===
      // When the approve path handed us a task DAG and the flag is on, run the structured
      // honest-green-gated executor — one runLocalAgentLoop per task, per-task ORACLE gate,
      // checkpoint seams (E4) — INSTEAD of a single free-form prose turn. Reuses the in-scope
      // chatFn/route/root/controller. Flag OFF / no tasks ⇒ falls through to the normal turn.
      if (Array.isArray(dagTasks) && dagTasks.length > 0 && readDagExecutorEnabled()) {
        // autolearn E1 — did any task run a REAL verifier? Gates honest-green on the run-grain outcome.
        let dagAnyVerifier = false;
        let scripts = null;
        try {
          const res = await window.apolo?.file?.read?.({ root, filePath: 'package.json' });
          scripts = res?.content ? (JSON.parse(res.content).scripts || null) : null;
        } catch { /* no scripts → acceptance/console only */ }
        const runCommand = (p) => window.apolo.shell.exec({ ...p, cwd: p.cwd || root });
        const previewConsole = (typeof window !== 'undefined' && window.apolo?.preview?.console)
          ? previewConsoleVerifier(window.apolo.preview)
          : null;
        // arch-fable5 A3 (audit-hardened) — when enabled, run the acceptance check through a CLEAN
        // context (its own AbortController, independent of the builder's signal) AND alongside a
        // HELD-OUT, agent-invisible verifier (the node --check syntax floor on the task's changed
        // files). Green then requires BOTH the agent-visible checks and the held-out one to pass —
        // a build that games the visible verifiers but breaks syntax goes RED. Flag OFF (default)
        // ⇒ freshVerify yields null ⇒ verifyTask uses the inline runOracle ⇒ byte-identical to today.
        const freshOn = readFreshVerifierEnabled();
        const freshVerify = makeFreshVerifier({
          enabled: freshOn,
          runOracleFresh: ({ verifiers, taskText }) => runOracle({
            verifiers,
            taskText,
            signal: new AbortController().signal,
          }),
        });
        // role-scaffold-twv — the WORKER: one agent turn per task (mirrors the self-repair sub-run
        // pattern). Appends the Thinker note to the prompt ONLY when present; when the scaffold is OFF the
        // note is never set, so this is byte-identical to the pre-sprint runTask.
        // #5 — respostas do usuário (do texto de aprovação) chegam em CADA task do DAG.
        const userDecisions = extractUserDecisions(text);
        // RCA lentidão fix G — digest da fase de plano mata o cold-start das tasks (planContextDigest).
        const planDigest = buildPlanContextDigest(messages || []);
        // fixes A/H/I/B — o runner (sessionWorkspace/dagTaskRunner) injeta excludeTools, progresso vivo e modo wave.
        // deadline:false — o teto por task (fix H) envolve o SEAM INTEIRO abaixo (cobre o Thinker também).
        const baseRunTask = makeDagTaskRunner({
          deadline: false,
          runLoop: runLocalAgentLoop,
          loopArgsForTask: (task) => ({
            chatFn,
            baseUrl: route.baseUrl,
            model: route.model,
            root,
            toolPolicy: defaultToolPolicy(), // E0 deny-list intact per task
            sessionId: asSessionId(session.id),
            messages: buildDagWorkerMessages(task, { userDecisions, doctrine: codeDoctrineBlock(session, { planPropose, dagTasks }, buildRulesInjection), planDigest }), // E4-T01 doctrine + fix G digest do plano (system estável)
            taskText: task.name || task.goal || task.id,
            currentTaskId: task.id,
            permissionsMode: 'ignore',
            // guard PR #618 — o gate chat-ship (git push/gh pr pedem aprovação) vale
            // TAMBÉM nos workers do DAG de uma sessão de chat.
            sessionKind: sessionKindOf(session),
            requestApproval: requestToolApproval,
            maxIterations: DEFAULT_MAX_ITERATIONS,
            // E8-T07 — per-task worker can recall (session_search) + persist (memory) too.
            ...(harvestExtraTools.length ? { extraTools: harvestExtraTools } : {}),
          }),
          onEvent,
          dispatchProgress: (p) => dispatchPlan(session.id, p),
          parentSignal: controller.signal,
        });
        // Prescribed Thinker→Worker→Verifier (FORM, not learned). E13-T04 — decisão POR
        // TAREFA: flag global OU ≥1 task complexa liga o scaffold; task simples devolve
        // {} no assign e degrada pra Worker puro (custo zero). Verifier segue o oracle.
        const rawSeam = dagWantsScaffold(dagTasks, readRoleScaffoldEnabled())
          ? composeRoleScaffold({
            assign: roleAssignForTask(assignRoles({ pool: COUNCIL_AGENTS, currentModelId: route.model }), readRoleScaffoldEnabled()),
            // THINKER: read-only pre-pass (mutating tools dropped, 3 iterações). signal =
            // efetivo do scaffold (deadline da task); sink cala deltas em wave paralela.
            think: async ({ task, model, signal, runCtx }) => {
              try {
                const r = await runLocalAgentLoop({
                  chatFn,
                  baseUrl: route.baseUrl,
                  model,
                  root,
                  toolPolicy: defaultToolPolicy(),
                  sessionId: asSessionId(session.id),
                  messages: [{ role: 'user', content: `Você é o THINKER. NÃO edite arquivos. Em até 6 linhas, descreva a abordagem da task (riscos, arquivos prováveis, passos). Só o plano.\n\nTask: ${task.name || task.goal || task.id}${task.acceptance ? `\nAcceptance: ${task.acceptance}` : ''}` }],
                  taskText: `thinker: ${task.name || task.id}`,
                  currentTaskId: task.id,
                  permissionsMode: 'deny',
                  excludeTools: ['update_todo'], // fix A — thinker também não flipa board
                  readOnly: true,
                  maxIterations: 3,
                  signal,
                  onEvent: makeThinkerEventSink(task.id, onEvent, runCtx),
                });
                return r && r.finalText ? String(r.finalText) : null;
              } catch { return null; }
            },
            work: baseRunTask,
            onEvent,
            signal: controller.signal,
          })
          : baseRunTask;
        // fix H — UM deadline por task cobrindo o seam inteiro (thinker + worker).
        const runTaskSeam = withDagDeadline(rawSeam, { parentSignal: controller.signal, onTimeout: (task) => pushMsg(taskTimeoutNotice(task)) });
        const seams = {
          runTask: runTaskSeam,
          // verifyTask: the per-task ORACLE (acceptance → verifier, else project verifiers; +M1/M2 +A3
          // held-out). honest-green is the SOLE judge. Extracted to a pure, unit-tested factory
          // (audit: decompose the god-component) — behavior-identical to the former inline closure.
          verifyTask: buildVerifyTask({
            verifiersForTask, root, runCommand, scripts, previewConsole, buildDefaultVerifiers,
            runOracle, freshVerify, signal: controller.signal, observe,
            noSilentGreen: readNoSilentGreenEnabled(), freshOn,
            onVerifierRan: () => { dagAnyVerifier = true; },
          }),
          signal: controller.signal,
          // #9 — sincroniza o TodoDropdown AO VIVO com o DAG (task_started/pass/fail → todo_status).
          onEvent: (evt) => { const u = taskEventToTodoStatus(evt); if (u) dispatchPlan(session.id, { type: 'todo_status', ...u }); },
          // fix B — wave atômica: independentes juntas (local ⇒ 1, GPU serializa; cloud ⇒ 3).
          concurrency: readDagParallelEnabled() ? dagConcurrencyForRoute(route) : 1,
        };
        // E4 — live checkpoints: snapshot the tree on each honest-green task, restore the
        // last-green (N-1) when a task fails. Non-destructive git (stash create / checkout
        // -- .), behind apolo_checkpoints (default ON). useTaskExecutor wraps save to emit
        // apolo:checkpoint for the Guard panel.
        if (readCheckpointsEnabled()) {
          const cp = makeGitCheckpoint({ root, runCommand });
          seams.saveCheckpoint = () => cp.save();
          seams.restoreCheckpoint = (id) => cp.restore(id);
        }
        pushMsg({ role: 'system', content: `▶️ Executando ${dagTasks.length} tasks como DAG honest-green (ordem topológica, uma por vez)…` });
        let dagRes;
        try {
          dagRes = await taskExec.runGraph(dagTasks, seams);
        } catch (e) {
          dagRes = { blocked: true, reason: e?.message || 'erro', done: [], failed: [] };
        }
        runOk = !dagRes.blocked && !controller.signal.aborted;
        const summary = dagSummaryLine(dagRes, dagTasks); // WSX — pura (loop/dagPlanBridge); trata blocked_permission
        pushMsg({ role: 'agent', content: summary, model: route.model });
        try {
          await persistMessages(session.id, buildPersistedMessages({ userText: text, includeUser: false, finalText: summary }));
        } catch { /* persistence best-effort */ }
        // autolearn E1-WS2 — feed the DAG honest-green result into the SAME learning ledger the
        // single-turn path uses (it was DROPPED at this early return — the system's best signal).
        // Behind apolo_dag_learning (default ON; OFF ⇒ no DAG outcome = today). Best-effort, never throws.
        if (route?.model && readDagLearningEnabled()) {
          const dagOutcome = mapDagResultToOutcome(dagRes, dagTasks, { anyVerifierRan: dagAnyVerifier });
          const dagAgentResult = {
            ok: !dagRes.blocked,
            stopReason: dagOutcome.stopReason,
            iterations: dagOutcome.iterations,
            autoCorrect: { reachedGreen: dagOutcome.reachedGreen },
          };
          setRunAgentMeta(session.id, { stopReason: dagOutcome.stopReason });
          void recordTurnTrace(buildTurnTraceRecord({
            runId: session.id,
            turnId: String(turnStartedAt),
            input: text,
            model: route.model,
            agentResult: dagAgentResult,
            toolCalls: [],
            startedAt: turnStartedAt,
          })).catch(() => {});
          const payload = buildRunOutcomePayload(session.id, {
            model_id: route.model,
            iterations: dagOutcome.iterations,
            stopReason: dagOutcome.stopReason,
            reachedGreen: dagOutcome.reachedGreen,
            text: originalTask, // WS-F/F9 — classifica o task_type real (não mais 'fix' fixo)
          });
          void recordOutcome(payload).then((r) => { if (r?.id) setLastOutcomeId(session.id, r.id); }).catch(() => {});
        }
        refreshSessions();
        return; // executor path done — skip the single free-form turn; finally still cleans up
      }

      do {
        if (autoRound > 0) {
          pushMsg(autoContinueStatusMessage(autoRound));
        }
        const liveMessages = autoRound === 0 ? (messages || []) : getRunSnapshot(session.id).messages;
        // E3 (live-verify) — auto-compact por tokens ANTES do send (flag OFF ⇒ mesma ref).
        const compacted = autoCompactBeforeSend({ sessionId: session.id, liveMessages, model: route.model, pushMsg, fresh: autoRound === 0 });
        const history = voiceCapHistory(vt, clearConsumedIfEnabled(buildAgentHistory(compacted))); // E4; voz cap
        const roundTask = voiceRoundTask(vt, { autoRound, originalTask, planPropose, buildTaskAnchor, buildAutoContinuePrompt });
        const userContent = buildUserContent(roundTask, autoRound === 0 ? attachments : undefined);
        // FR (daily-auto-improve) — inject the active agent-rule books (Settings → Profile)
        // as a CODING DOCTRINE system block, so manual Code turns follow the same best
        // practices as the daily self-improve run. Stable across turns ⇒ cache-friendly.
        // Empty when the user disabled every book ⇒ byte-identical to before.
        // FASE-12 E1-T05 — helper puro pula a doutrina de CÓDIGO (16.8KB) em chat conversacional (gemma-4b estourava contexto).
        const askMode = isCollaborationReadOnly(useAgentStore.getState().code.chatModes[session.id], session.flags); // E7-T02
        const { doctrineReadOnly, loopReadOnly } = voiceReadFlags(vt, askMode);
        const agentRulesBlock = codeDoctrineBlock(session, { planPropose, dagTasks, readOnly: doctrineReadOnly }, buildRulesInjection); // E4-T03: Ask/voz pula doutrina
        // Plan/Ask/Voz — diretiva SYSTEM (bolha limpa). Voz = só data+MODO VOZ.
        const sysWithBrain = voiceComposeSystem(vt, { heavySys: sys, agentRulesBlock, ambientBlock, directive: modeDirective(planPropose, askMode, vt.voiceMode), dateLine: currentDateLine() });
        const convo = capConversation([
          ...turnSystemMessages(sysWithBrain, routeDecision),
          ...history,
          { role: 'user', content: userContent },
        ]);
        const permClassify = readPermClassifierEnabled() ? buildPermissionClassifier({ chatFn, model: route.model, baseUrl: route.baseUrl, timeoutMs: 600 }) : null;

        agentResult = await runWithSupervisor({
          chatFn,
          baseUrl: route.baseUrl,
          model: route.model,
          options: {},
          root,
          toolPolicy: defaultToolPolicy(), // E0 — deny secret dirs (.ssh/.aws/…) at the tool gate
          // S4 (E4) — pre-tool auto-review, active only when .apollo/permissions.json exists
          // (ref reads the latest loaded value inside this stable callback).
          autoReview: buildAutoReviewConfig(permissions.ref.current, permClassify, buildE6Hooks()), // EPIC-CLASSIFY-1 + E6 hooks (flag-gated)
          sessionId: asSessionId(session.id),
          sessionKind: voiceSessionKind(vt, sessionKindOf(session)), // F13-E7-T06; voz⇒chat conversacional
          messages: convo,
          taskText: originalTask,
          excludeTools: voiceExcludeTools(vt), // voz: '*' zera tools (sem rodada de tool)
          // E8-T07 harvest + E13 subagentes tipados (explore/code_review) no turno principal.
          ...(mainExtraTools.length ? { extraTools: mainExtraTools } : {}),
          // wire-dead-engines E1 — verify gate WITH TEETH: lint/types/smoke (+ console)
          // run by default. Absent (flag off / no project scripts) ⇒ omitted = today.
          ...(autoCorrectCfg ? { autoCorrect: autoCorrectCfg } : {}),
          // Turn Role Router overrides (pure, tested in applyDecision.test.js): null
          // decision ⇒ { readOnly:false, permissionsMode: permissionsMode || 'ask' } —
          // byte-identical to today. Router only ever TIGHTENS (read-only / ask).
          ...resolveLoopOverrides(routeDecision, permissionsMode),
          // Plan Mode v2 — HARD gate: planMode ⇒ readOnly, so the loop's existing
          // MUTATING_TOOLS hard-deny blocks write/edit/run regardless of the model or
          // permissions_mode. The model's (non-)compliance with the directive is moot.
          ...(planPropose ? { readOnly: true, planMode: true } : {}),
          // #11/#12 Ask + conversa por voz → read-only (voz também cap de iterações).
          ...(loopReadOnly ? { readOnly: true } : {}),
          maxIterations: turnIterationLimit(routeDecision, vt),
          signal: controller.signal,
          requestApproval: requestToolApproval, // E6-WS1-T02 — dialog não-bloqueante (era window.confirm síncrono)
          onEvent,
        }, runLocalAgentLoop, { route, providerContext: { llm_adapter_id: route.llmAdapterId || null, reasoningEffort: route.reasoningEffort, spec } });

        // S4 (E5) — feed local auto-review telemetry to the Guard-rails panel (counts only, no PII).
        if (agentResult?.autoReview && agentResult.autoReview.total > 0) {
          window.dispatchEvent(new CustomEvent('apolo:autoReview', {
            detail: { sessionId: session.id, metrics: agentResult.autoReview },
          }));
        }

        // E3 (live-verify) — overflow ⇒ compacta (marker) e retenta o round 1x.
        if (compactOnOverflow({ sessionId: session.id, agentResult, liveMessages: getRunSnapshot(session.id).messages, pushMsg })) { autoRound += 1; continue; }

        const willAutoContinue = shouldAutoContinue(agentResult, autoRound) && !controller.signal.aborted;
        const notice = terminalNotice({
          ...agentResult,
          autoContinuing: willAutoContinue,
          autoContinueRounds: autoRound,
        });
        if (notice) pushMsg(notice);

        if (!willAutoContinue) break;
        autoRound += 1;
      } while (true);
      pipeline.flush(); // E5-WS1 — aterrissa o rabo do stream (turno pode acabar em token)

      // Sinal terminal já emitido dentro do loop quando aplicável.

      // E2 (super-marathon) — attach the verification SEAL to the final agent bubble.
      // Live-only render (consistent with tool blocks): the flag-gated event set
      // lastVerificationReport; when absent (flag OFF) this is a no-op, byte-identical.
      if (pipeline.lastVerificationReport && pipeline.lastAgentMsgId) {
        setMessages((m) => m.map((x) => (x.id === pipeline.lastAgentMsgId
          ? { ...x, metadata: { ...x.metadata, verificationReport: pipeline.lastVerificationReport } }
          : x)));
      }

      // Persiste best-effort (user turn + tool turns + resposta final) pro
      // histórico E o rail de Saídas (Fase 1 · E1) sobreviverem ao reload. A
      // montagem/ordem vive em lib/artifacts (pure, testada); aqui só itera+POST.
      // turnToolNames: capturado só aqui (caminho principal); DAG/team retornam antes → []
      // (byte-idêntico). turnToolCount lê pipeline.toolRecords.size VIVO no finally.
      turnToolNames = [...pipeline.toolRecords.values()].map((r) => ({ name: r.tool_name })).filter((t) => t.name); // E4
      try {
        const toPersist = buildPersistedMessages({
          userText: text,
          includeUser: false, // já persistida no dispatch (evita duplicata)
          toolRecords: [...pipeline.toolRecords.values()],
          finalText: agentResult?.finalText,
          reasoning: pipeline.finalReasoning, // HRN3-T01 — persist the reasoning summary
          error: turnErrorOf(agentResult), // E2-WS3-T01 — turno falho deixa registro persistente
        });
        await persistMessages(session.id, toPersist);
      } catch (e) {
        console.warn('[SessionWorkspace] turn message persistence failed to build', e);
      }
      refreshSessions();

      // === Self-Repair Loop (E4) — opt-in (default OFF), ADDITIVE post-turn block ===
      // A DRIVER over runLocalAgentLoop (agentLoop.js untouched). Off ⇒ this no-ops.
      // Only runs when the turn actually wrote code (write_file/apply_edit).
      try {
        const touchedCode = [...pipeline.toolRecords.values()].some(
          (r) => r.tool_name === 'write_file' || r.tool_name === 'apply_edit',
        );
        // Feed the Turn Role Router's next-turn signal (build-continuation detection).
        lastTurnTouchedCodeRef.current = touchedCode;
        if (selfRepair.enabled && touchedCode && !controller.signal.aborted) {
          const check = async ({ root: r }) => {
            let pkg = null;
            try {
              const res = await window.apolo?.file?.read?.({ root: r, filePath: 'package.json' });
              pkg = res?.content ? JSON.parse(res.content) : null;
            } catch { /* no/invalid package.json → no verify command */ }
            const cmd = await detectVerifyCommand({ root: r, readPackageJson: () => pkg });
            return runCheck({ root: r, cmd, runCommand: (p) => window.apolo.shell.exec({ ...p, cwd: p.cwd || r }) });
          };
          // Fix/troubleshoot sub-runs reuse the loop autonomously (the user opted in
          // via the toggle); E0's toolPolicy/SSRF guards still apply.
          const repairRunAgent = ({ task, signal: s }) =>
            runLocalAgentLoop({
              chatFn,
              baseUrl: route.baseUrl,
              model: route.model,
              root,
              toolPolicy: defaultToolPolicy(),
              sessionId: asSessionId(session.id),
              messages: [{ role: 'user', content: task }],
              taskText: task,
              permissionsMode: 'ignore',
              maxIterations: DEFAULT_MAX_ITERATIONS,
              signal: s || controller.signal,
              // E8-T07 — repair sub-runs can recall prior fixes (session_search) / persist notes.
              ...(harvestExtraTools.length ? { extraTools: harvestExtraTools } : {}),
              onEvent: () => {},
            });
          const repairResult = await selfRepair.runFromTurn(
            { finalText: agentResult?.finalText, touchedCode },
            { selfRepairLoop, root, runAgent: repairRunAgent, check, signal: controller.signal },
          );
          setRepairReport(repairResult?.report || null);
          if (repairResult?.escalated) {
            pushMsg({ role: 'system', content: 'Auto-reparo: esgotou as tentativas — escalando (precisa de planejamento). Veja o painel de auto-reparo.' });
          } else if (repairResult?.ran && repairResult?.assurance) {
            pushMsg({ role: 'system', content: 'Auto-reparo: a verificação do projeto passou.' });
          }
        }
      } catch { /* self-repair é best-effort — nunca quebra o turno */ }
      runOk = true; // chegou ao fim do try sem lançar → run concluiu
    } catch (e) {
      // O loop/chat rejeitou (ex.: rota local mas IPC ausente → chatFn lança;
      // ou /agent-turn caiu). Mostra o erro em vez de deixar a Promise propagar:
      // sem isso, o caller (auto-send) virava unhandled rejection e o indicador
      // "Agente trabalhando…" ficava preso (setLocalRunning(false) era pulado).
      setError(e?.message || 'Falha ao executar o agente');
    } finally {
      // WS-B — o turno assentou (fim/erro/abort): run.settle desarma o watchdog e, SÓ se
      // ESTE run ainda é o atual (same-controller guard — sem ele um run antigo que
      // termina tarde apagaria o indicador/controller do run NOVO, CodeRabbit #61),
      // reseta o control plane (spinner off, liveStatus null, controller limpo, bolhas
      // de streaming órfãs fechadas). true ⇒ rode os efeitos run-owned (TTS/plano/outcome).
      if (run.settle()) {
        // Sprint v11 E15 — hands-free TTS playback. O agente loop real não
        // passa pelo useStreamingChat (mantido só pra indicadores de UI), então
        // o evento apolo:handsFreeReply QUE ESTÁ LÁ nunca dispara neste caminho.
        // Disparamos aqui: o hook ignora quando o loop de voz está off, então é
        // seguro disparar incondicionalmente. agentResult.finalText é a resposta
        // final do LLM (mesma string que foi pro chat).
        try {
          if (runOk && agentResult?.finalText && agentResult.finalText.trim()) {
            window.dispatchEvent(new CustomEvent('apolo:handsFreeReply', {
              detail: { text: agentResult.finalText },
            }));
          }
        } catch { /* best-effort — nunca quebra o finally */ }
        // Planning Mode — drive the PlanState by the OBJECTIVE run outcome (not the
        // agent's word): a clean finish marks the approved plan done; an abort/error
        // blocks the active todo. No-op unless a plan is executing.
        // #4/P1b — gate honesto (planHonestOutcome) + RunOutcome canônico (runResultEvent): não fabrica 100%.
        dispatchPlan(session.id, buildRunResultEvent({ finished: runOk, aborted: controller.signal.aborted, agentResult, at: Date.now(), root: sessionRoot(session) }));
        // Plan Mode v2 propose-turn (E1-WS2-T01 — seam sessionWorkspace/planProposal):
        // surface the settled plan com Aprovar/Rejeitar. PREFERE o structured plan do
        // present_plan (agentResult.plan) e cai pro parse da prosa "## Plan"; seta o
        // pending plan (PlanProposalCard), despacha o propose (TodoDropdown) e projeta no
        // board de Tasks. Toda a lógica mora no módulo deep.
        proposePlanFromOutcome({
          agentResult,
          planPropose,
          runOk,
          aborted: controller.signal.aborted,
          sessionId: session.id,
          getMessages: () => getRunSnapshot(session.id).messages || [],
          setPendingPlan,
          dispatchPlan,
          setCodeTasks: (p) => useAgentStore.getState().setCodeTasks?.(p),
          setTodoOpen,
        });
        // LRN2-T02 — close the agent_outcomes ledger at run-end. Fire-and-forget
        // (recordOutcome never throws): one run-grain row {session, model, task}
        // that V3/V4 read in aggregate. The external signal (merge/revert) is
        // filled later (LRN2-T04). Only for the run that actually owns the turn.
        if (route?.model) {
          setRunAgentMeta(session.id, { stopReason: agentResult?.stopReason });
          // E2 (Camada V) — telemetria de turno: 1 rec mensurável por turno
          // (modelo/iterações/stopReason/nº tools/duração) pro session_events
          // via o canal observe já existente. Best-effort, só métricas.
          observe('turn_telemetry', buildTurnTelemetry({
            model: route.model,
            agentResult,
            toolCount: pipeline?.toolRecords.size || 0, // vivo (cobre DAG/team early-return, como o antigo update no onEvent); 0 se o turno saiu antes do pipeline (ex.: no-root)
            startedAt: turnStartedAt,
          }));
          // E4 (self-improve-hardening) — persist the FULL, replayable TurnTrace to
          // D1 (the real consumer) so a FAILED run can be re-run deterministically
          // (oracle/replay.js) + surfaced in the bad-runs view. Best-effort; a
          // persist failure is logged (the worker also 500s + logs), never silent.
          void recordTurnTrace(buildTurnTraceRecord({
            runId: session.id,
            turnId: String(turnStartedAt),
            input: text,
            model: route.model,
            agentResult,
            toolCalls: turnToolNames,
            startedAt: turnStartedAt,
          })).then((r) => {
            if (!r?.ok && typeof console !== 'undefined') {
              console.warn('[turn-trace] persist failed', r?.error);
            }
            // PART A — live craft judge: after a GREEN turn persists, refine the
            // craft scores with the local LLM (best-effort; no-op without a local
            // model). The server-side Discovery (proxy) already ran via the
            // turn-trace route; the LLM call's latency lets it persist first.
            if (r?.ok && agentResult?.autoCorrect?.reachedGreen === true) {
              void runLocalCraftJudge({ runId: r.run_id || session.id }).catch(() => {});
            }
          });
          const payload = buildRunOutcomePayload(session.id, {
            model_id: route.model,
            iterations: agentResult?.iterations,
            stopReason: agentResult?.stopReason,
            // E1 (honest-green) — the oracle's VERIFIED verdict (undefined when no
            // verifier ran → the run is recorded as not_run, not a fabricated success).
            reachedGreen: agentResult?.autoCorrect?.reachedGreen,
            // WS-F/F9 — classifica o task_type real (não mais 'fix' fixo). No finally,
            // `originalTask` (declarado no try) está fora de escopo; usa o param `text`.
            text,
          });
          void recordOutcome(payload).then((r) => {
            if (r?.id) setLastOutcomeId(session.id, r.id);
            const syncRoot = sessionRoot(session);
            if (syncRoot) {
              void syncExternalSignalFromGit(session.id, syncRoot, { model_id: route.model });
            }
          });
        }
      }
    }
  }

  // Plan Mode v2 (E4) — APPROVE the structured plan: fire a continuation turn that
  // EXECUTES it. Plan mode OFF (so write/edit/run are allowed again), and the task
  // text embeds the numbered steps + the user's answers to the clarifying questions
  // so the executing agent has the full resolved brief. Clear the pending plan so the
  // card dismisses, mark the projected TodoDropdown plan as executing, then dispatch
  // through the SAME handleSend path the prose TodoDropdown approve already uses.
  const handlePlanApprove = useCallback(
    (answers) => {
      const plan = getPendingPlan(session?.id);
      const text = buildPlanApprovalText(plan, answers);
      // Sprint hgmq-finish P7 — persist-on-approval (Track C). The spec is
      // ephemeral in the Glass Box during the turn; on HUMAN approval it is
      // written to .apolo/specs/<slug>/{requirements,design,tasks}.md (the
      // approval gate is the write boundary). buildSpecFromPlan derives the
      // tasks from plan.tasks (DAG) OR plan.steps — present_plan emits `steps`,
      // so gate on EITHER (the old `plan.tasks`-only gate never fired for a
      // normal structured/prose plan → the Specs panel stayed empty). persistSpec
      // always calls setSpec, so the Glass Box fills even if the disk write is a
      // no-op (web / no IPC). Best-effort: never block approval on the spec.
      const planHasWork =
        plan &&
        ((Array.isArray(plan.tasks) && plan.tasks.length) ||
          (Array.isArray(plan.steps) && plan.steps.length));
      // wire-dead-engines E3 — derive the executable DAG from the same spec we persist, and
      // hand it to handleSend so the executor (not a prose turn) runs it. deps→depends_on via
      // adaptTasksForExecutor (Correction 1). Best-effort: a build failure ⇒ null ⇒ prose turn.
      let dagTasks = null;
      if (planHasWork) {
        try {
          const spec = buildSpecFromPlan(plan, { feature: plan.summary, answers });
          useAgentStore.getState().persistSpec?.(session?.id, spec);
          dagTasks = adaptTasksForExecutor(spec?.tasks?.derived || []);
        } catch { /* best-effort — never block approval on the spec/DAG build */ }
      }
      clearPendingPlan(session?.id);
      // Keep the projected todos coherent: if a plan was projected onto PlanState,
      // flip it to executing (no-op when there's no active plan).
      dispatchPlan(session?.id, { type: 'approve', at: Date.now() });
      // AUDIT 2026-07-02 (stale-closure): call via handleSendRef.current — it is
      // reassigned EVERY render, so the execute turn runs with the LATEST closure
      // (adapters loaded, current messages). Calling the memoized `handleSend`
      // captured at mount routed local models to cloud (adapters=[]) and executed
      // the plan blind to the conversation (empty messages snapshot).
      handleSendRef.current({
        text,
        model: session?.model,
        permissions_mode: session?.permissions_mode,
        plan_mode: false,
        dagTasks,
        executeApproved: true, // human approved → EXECUTE (write/edit/run), never re-propose
      }).catch(() => {});
    },
    // handleSendRef is a stable ref (points at the current-render handleSend).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session?.id, session?.model, session?.permissions_mode],
  );

  // Plan Mode v2 (E4) — REJECT the plan: fire a continuation turn that RE-PLANS.
  // Plan mode STAYS ON so the agent re-calls present_plan (which sets a fresh pending
  // plan); the task text carries the user's one-line feedback. Clear the current
  // pending plan so the stale card dismisses while the re-plan runs.
  const handlePlanReject = useCallback(
    (feedback) => {
      const text = buildPlanRejectText(feedback);
      clearPendingPlan(session?.id);
      dispatchPlan(session?.id, { type: 'reject' });
      // AUDIT 2026-07-02 (stale-closure): via handleSendRef.current (fresh closure).
      handleSendRef.current({
        text,
        model: session?.model,
        permissions_mode: session?.permissions_mode,
        plan_mode: true,
      }).catch(() => {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session?.id, session?.model, session?.permissions_mode],
  );

  // E6-WS1-T02 — executor event listeners (approveDag, restoreCheckpoint, taskGraph)
  // extracted to useExecutorEvents. stopAgent + ESC to useStopAgent.
  useExecutorEvents({ session, handleSendRef, handlePlanApprove });
  const stopAgent = useStopAgent({
    sessionId: session?.id, streaming, localRunning, generatingSpec,
    specGenAbortRef, abort, setGeneratingSpec, setLiveStatus,
  });

  // E6-WS1-T01 — auto-first-message delegated to useTurnDispatch; wire handleSendRef
  // so the hook can invoke handleSend without closing over the full closure.
  // The hook passes { text }; model vem do pick VIVO (ui.model), não do session.model
  // stale — ver resolveDispatchModel (bug 2026-07-13: turno rodava o modelo salvo).
  handleSendRef.current = (args) =>
    handleSend({ ...args,
      model: resolveDispatchModel(args.model, useAgentStore.getState().code.ui?.model, session?.model),
      permissions_mode: args.permissions_mode || session?.permissions_mode });

  // Fila de envio (BUG: mandar msg enquanto roda não podia enfileirar) — despacha o
  // próximo envio enfileirado quando o turno termina (fim/Stop/watchdog).
  const drainSessionId = session?.id;
  useQueuedSendDrain({ sessionId: drainSessionId, running: localRunning, handleSendRef });
  // DeerFlow #1 follow-up — botões do turn_blocker (Continuar/Tentar de novo/verificação)
  useBlockerActions({ sessionId: drainSessionId, handleSendRef });

  if (!session) return null;

  // Run DE FATO ativo (composer mostra Stop): alimenta o SessionComposer e a pill
  // de tempo do Plano (que só tica com run vivo — nunca num plano fantasma parado).
  const runActive = localRunning || streaming || generatingSpec;

  const headerMenuMeta = {
    directory: session.directory,
    name: session.name,
    autoSave: !!session.flags?.auto_save_on_end,
    useBrain: !!session.flags?.use_pedro_brain,
    statusLabel: localRunning || streaming ? 'rodando' : 'pronto',
    modelLabel: session.model ? getProviderLabel(session.model) : null,
    running: localRunning || streaming,
  };

  return (
    <div className="rx-shell flex h-full flex-1 overflow-hidden" data-rx-glow={toolbar.glowTheme}>
      <div className="flex min-w-0 flex-1 flex-col" style={{ '--chat-col': chatColMaxWidth }}>
        {/* relative z-30: header acima do corpo do chat. (Pedro 2026-06-25,
            sprint cursor-strip) UMA única linha contendo, da esquerda pra
            direita:
              · SessionFileTabs (abas de arquivo + Plano/Contexto) — esquerda
              · breadcrumb "Users › pedromleal › Apolo" — ao lado dos ícones
              · cluster de ícones [</>][▶|][👁] — direita
            Antes eram 3 rows empilhadas. */}
        {/* (Pedro 2026-06-25, sprint cursor-strip T5 minimalism) — gap reduzido
           (1) e padding vertical menor (py-1). Active state dos botões abaixo
           usa surface3 neutro em vez de bg-accent/12 (Pedro lê accent fora do
           ativo como erro / acende prefers-contrast). */}
        <header className="workbench-session-header relative z-30 flex flex-col bg-apolo-surface1/80 backdrop-blur-md">
          <div className="flex items-center gap-1 px-3 py-1 lg:gap-2 lg:px-4">
            {/* tab da sessão na MESMA posição (esquerda). O wrapper
               data-rx-tabs-inline neutraliza o background/border/height próprio
               do rx-ctxtabs quando ele vive embutido no header. */}
            <div className="flex min-w-0 flex-1 items-center" data-rx-tabs-inline>
              {!ideMode && <button className="workbench-session-tab" aria-pressed={!planTabOpen && !contextTabOpen} onClick={() => { setPlanTabOpen(false); setContextTabOpen(false); }}><MessageSquare size={14} />Conversa</button>}
              <SessionFileTabs
                files={[]}
                onBack={onBack}
                extraTabs={[
                  ...(!ideMode && planActionable
                    ? [{
                        key: 'plan',
                        label: 'Plano',
                        icon: ListTodo,
                        active: planTabOpen,
                        badge: `${planDone}/${planCount}`,
                        title: 'Plano da sessão — lista de tarefas',
                        testid: 'session-plan-tab',
                        onClick: () => {
                          setContextTabOpen(false);
                          setPlanTabOpen((v) => !v); // toggle: clicar de novo volta pro chat
                        },
                      }]
                    : []),
                  ...(contextAvailable
                    ? [{
                        key: 'context',
                        label: 'Contexto',
                        icon: Library,
                        active: contextTabOpen,
                        badge: contextCount > 0 ? String(contextCount) : null,
                        title: 'Contexto — anexos e conhecimento da sessão',
                        testid: 'session-context-tab',
                        onClick: () => {
                          setPlanTabOpen(false);
                          setContextTabOpen((v) => !v); // toggle: clicar de novo volta pro chat
                        },
                      }]
                    : []),
                ]}
              />
            </div>

            {/* (Pedro 2026-06-25) — breadcrumb "Users › pedromleal › Apolo"
               removido do canto superior direito a pedido do Pedro. */}

            <SessionViewControls ideMode={ideMode} setIdeMode={setIdeMode} outputsCollapsed={outputsCollapsed} toggleOutputs={toggleOutputs}
              previewPanelOpen={previewPanelOpen} togglePreviewPanel={togglePreviewPanel}
              onSearch={() => { setPlanTabOpen(false); setContextTabOpen(false); toolbar.openSearch(); }} />
          </div>
        </header>

        <SessionRunRecovery key={session.id} sessionId={session.id} />
        {(error || streamError) && (
          <div role="alert" aria-live="assertive" aria-atomic="true" className="border-b border-apolo-error/30 bg-apolo-error/10 px-3 py-1 text-xs text-apolo-error">
            {error || streamError}
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* E2 — chat/IDE à esquerda; rail de Saídas persistente à direita. */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {ideMode ? (
              <CodeIdePanel session={session} />
            ) : (
              <>
                {/* (sprint plan-tab / context-tab) — as abas "Plano"/"Contexto"
                    trocam o feed do chat pela view respectiva. O composer abaixo
                    fica (reuso), então dá pra seguir conversando/alimentando. */}
                {contextTabOpen ? (
                  <ContextTabView contextFiles={contextFiles} sessionId={session.id} />
                ) : planTabOpen ? (
                  <PlanTabView todos={planTodos} {...(planState || {})} running={runActive} />
                ) : (
                <div className="relative flex min-h-0 flex-1 flex-col">
                  <div
                    ref={chatScrollRef}
                    onScroll={onChatScroll}
                    className="workbench-chat-feed flex flex-1 flex-col gap-2 overflow-y-auto px-3 pt-2"
                  >
                    {/* chatContentRef wraps EVERYTHING that grows — committed
                        messages (ExecutionChat) AND the live StreamingMessage /
                        LiveActivity siblings — so the ResizeObserver pins to the
                        latest line as the answer streams. */}
                    <div ref={chatContentRef} className="flex flex-col gap-2">
                      <ExecutionChat
                        messages={messages}
                        loading={loading}
                        model={session.model}
                        sessionId={session.id}
                        toolbar={toolbar}
                        hideToolbarIcons
                        manageScroll={false}
                      />
                      {/* E4-WS2: live siblings render live, model-driven data OUTSIDE
                          ExecutionChat's ChatErrorBoundary — a throw here used to escape to
                          AppErrorBoundary (full reload). Contain it (auto-reset per turn). */}
                      <GenericErrorBoundary resetKey={messages.length}>
                      {streaming && <StreamingMessage text={streamingMessage} />}
                      {streaming && (
                        <button
                          type="button"
                          onClick={abort}
                          className="self-end rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-xs text-apolo-textSecondary transition hover:border-apolo-accent hover:text-apolo-accent"
                        >
                          Interromper stream
                        </button>
                      )}
                      {localRunning && <LiveActivity status={liveStatus} sessionId={session.id} />}
                      {/* Mensagens deixadas PENDENTES no meio do turno (estilo
                          claude.app): aparecem aqui esmaecidas + X pra cancelar;
                          o useQueuedSendDrain despacha quando o turno terminar. */}
                      {runState.queued?.length > 0 && (
                        <PendingMessages
                          items={runState.queued}
                          onCancel={(i) => removeQueuedAt(session.id, i)}
                        />
                      )}
                      {/* Self-improve por-sessão visível: pill "aprendi nesta sessão". */}
                      {learned && learned.at !== learnedDismissedAt && (
                        <LearnedPill learned={learned} onDismiss={() => setLearnedDismissedAt(learned.at)} />
                      )}
                      {approvalUi}
                      {/* Plan Mode v2 (E4) — the structured proposed plan. Rendered
                          INSIDE the GenericErrorBoundary so a malformed plan payload
                          can't black-screen the chat (the boundary resets per turn).
                          This is the PRIMARY approve surface; it supersedes the
                          prose-scraped TodoDropdown approve UI below. */}
                      {pendingPlan && !localRunning && (
                        <PlanProposalCard
                          summary={pendingPlan.summary}
                          steps={pendingPlan.steps}
                          questions={pendingPlan.questions}
                          onApprove={handlePlanApprove}
                          onReject={handlePlanReject}
                        />
                      )}
                      {turnDecisionVisible(turnRouter) && (
                        <TurnRoleIndicator decision={turnRouter.lastDecision} />
                      )}
                      {selfRepair.enabled && selfRepair.events?.length > 0 && (
                        <SelfRepairPanel
                          state={selfRepair.state}
                          cycles={selfRepair.events[selfRepair.events.length - 1]?.cycle ?? 0}
                          events={selfRepair.events}
                          report={repairReport}
                        />
                      )}
                      </GenericErrorBoundary>
                    </div>
                  </div>
                  {showJump && (
                    <button
                      type="button"
                      onClick={jumpChatToBottom}
                      aria-label="Ir pro fim — seguir a última mensagem"
                      className="absolute bottom-3 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-apolo-borderSubtle bg-apolo-surface2/95 px-3 py-1.5 text-[12px] font-medium text-apolo-textSecondary shadow-lg backdrop-blur transition hover:border-apolo-accent/40 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
                    >
                      <ArrowDown size={13} aria-hidden="true" />
                      Ir pro fim
                    </button>
                  )}
                </div>
                )}
                {/* (Pedro 2026-06-25, sprint cursor-strip-4 R4-2) — a barra git
                   virou o STATUS BAR de baixo, estilo Cursor: parent é flex-col
                   e o git-wrapper carrega `order-last`, então renderiza ABAIXO
                   do composer (e do todo dropdown) sem mover o JSX. CI + "N PRs
                   abertos" já vivem dentro da GitConnectionBar → caem juntos. */}
                <div className="workbench-chat-dock mx-auto flex w-full max-w-[var(--chat-col,76rem)] flex-col px-3">
                  {/* (Pedro 2026-06-25, sprint cursor-strip-4 round 6) — pt-1
                     (4px) + composer mb-1 (4px) = ~8px de gap. Pedro pediu pra
                     deixar o composer e esta barra git bem próximos. */}
                  {/* (Pedro 2026-06-25, fix drop-up atrás do composer) — `order-last`
                     posiciona a git bar ABAIXO do composer, mas no DOM ela vem ANTES;
                     a ordem de PINTURA segue o DOM, então o composer (depois) pintava
                     POR CIMA. O GLASS da git bar tem backdrop-blur (cria stacking
                     context próprio com z auto), prendendo os drop-ups z-50 dentro
                     dele — incapazes de subir acima do composer. `relative z-40` dá à
                     row da git bar um stacking context acima do composer (z auto), pra
                     os drop-ups (slug/branch/CI/“+”/PRs, que abrem pra cima) pintarem
                     SOBRE o composer. 40 fica acima dos menus do composer (z-30) e
                     abaixo de overlays/modais (z-50+). */}
                  <div className="workbench-project-footer relative z-40 order-last flex items-center gap-2 pb-1 pt-1">
                    {(() => {
                      // pos-s5 (Pedro) — these session controls live INSIDE the git
                      // box now (Auto-reparo / Roteamento were floating beside it).
                      // The git box is w-full so the row matches the composer width.
                      const sessionControls = (
                        <>
                          {/* E2b — project steering transparency chip */}
                          <SteeringIndicator count={steering.count} />
                          <button
                            type="button"
                            onClick={selfRepair.toggle}
                            aria-label="Alternar auto-reparo (verifica e conserta após o turno: CHECK → FIX → re-CHECK → escalar)"
                            aria-pressed={selfRepair.enabled}
                            title="Auto-reparo: roda a verificação do projeto após um turno que tocou código e conserta automaticamente (escala se esgotar)"
                            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 ${
                              selfRepair.enabled
                                ? 'border-apolo-accent/40 bg-apolo-surface3 text-apolo-textPrimary'
                                : 'border-apolo-borderSubtle bg-transparent text-apolo-textSecondary hover:bg-apolo-surface3'
                            }`}
                          >
                            <Wrench size={12} aria-hidden="true" />
                            Auto-reparo {selfRepair.enabled ? 'ligado' : 'desligado'}
                          </button>
                          <button
                            type="button"
                            onClick={turnRouter.toggle}
                            aria-label="Alternar roteamento por papel (classifica o turno e escolhe modelo/ferramentas/permissão: análise → barato + somente leitura, arriscado → confirmação)"
                            aria-pressed={turnRouter.enabled}
                            title="Roteamento por papel: antes de cada turno, escolhe o modelo e o modo certos pelo tipo da tarefa (análise/build/arriscado/conversa). Seu modelo fixado sempre vence."
                            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 ${
                              turnRouter.enabled
                                ? 'border-apolo-accent/40 bg-apolo-surface3 text-apolo-textPrimary'
                                : 'border-apolo-borderSubtle bg-transparent text-apolo-textSecondary hover:bg-apolo-surface3'
                            }`}
                          >
                            <Route size={12} aria-hidden="true" />
                            Roteamento {turnRouter.enabled ? 'ligado' : 'desligado'}
                          </button>
                        </>
                      );
                      return hideGit || (session.kind === 'chat' && !sessionRoot(session)) ? (
                        <ComposerOverflowMenu label="Ajustes da conversa" icon={SlidersHorizontal} align="left" triggerClassName="workbench-session-settings">
                          <div className="flex flex-col gap-2 p-1">{sessionControls}</div>
                        </ComposerOverflowMenu>
                      ) : (
                        <GitConnectionBar sessionId={session.id} session={session}>
                          {sessionControls}
                        </GitConnectionBar>
                      );
                    })()}
                  </div>
                  {/* Prose-scraped TODO plan + its inline approve. SUPPRESSED while a
                      STRUCTURED pending plan exists — the PlanProposalCard above is the
                      single approve surface then (no two competing approve buttons). */}
                  {!pendingPlan && !planTabOpen && !contextTabOpen && planState?.todos?.length > 0 && (
                    <div className="mb-2">
                      <TodoDropdown
                        planState={planState} running={runActive}
                        open={todoOpen}
                        onToggle={() => setTodoOpen((v) => !v)}
                        onApprove={() => {
                          // HARD gate → executing, then auto-run the approved plan.
                          dispatchPlan(session.id, { type: 'approve', at: Date.now() });
                          handleSend({
                            text: `${EXECUTE_APPROVED_PREFIX}\n\nMarque cada passo com update_todo({id,status:"active"}) ao começar e ({id,status:"done"}) ao concluir.`,
                            model: session.model,
                            permissions_mode: session.permissions_mode,
                            plan_mode: false,
                            executeApproved: true, // TodoDropdown approve → EXECUTE, not re-propose
                          }).catch(() => {});
                        }}
                        onReject={() => dispatchPlan(session.id, { type: 'reject' })}
                      />
                    </div>
                  )}
                  <SessionComposer
                    onSubmit={handleSend}
                    sessionId={session.id}
                    running={runActive}
                    onStop={stopAgent}
                    // Arquivos do painel Contexto no menu do `@` (picar → anexa à msg).
                    contextFiles={contextFiles}
                    // Enviar enquanto roda → ENFILEIRA (não perde, não cancela). O
                    // turno atual segue; a fila despacha no fim (ou após Stop).
                    onQueue={(payload) => enqueueSend(session.id, payload)}
                    queuedCount={runState.queuedCount}
                    onOpenContext={() => {
                      setPlanTabOpen(false);
                      setContextTabOpen(true);
                    }}
                  />
                </div>
              </>
            )}
          </div>
          {/* Coluna direita de Saídas/Canvas/Menu — renderiza SÓ quando aberta.
             O antigo rail de ícones colapsado foi removido; abre/fecha pelo
             cluster no header do chat (Editor/Chat · Saídas · Design). */}
          {!outputsCollapsed && (
            <aside
              aria-label="Saídas, Canvas e Menu"
              className={`hidden shrink-0 flex-col transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex ${
                rightView === 'canvas' ? 'w-[460px]' : rightView === 'menu' ? 'w-80' : rightView === 'tasks' ? 'w-[330px]' : 'w-72'
              }`}
            >
              <>
                  {/* E8 — toggle Saídas | Canvas | Menu + colapsar no topo da coluna direita */}
                  <div className="flex shrink-0 items-center justify-between gap-2 px-2 py-1.5">
                    <div className="inline-flex rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-0.5 text-[11px]">
                      {[
                        ['tasks', 'Tasks'],
                        ['saidas', 'Saídas'],
                        ['canvas', 'Canvas'],
                        ['menu', 'Menu'],
                      ].map(([id, label]) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setRightView(id)}
                          aria-pressed={rightView === id}
                          className={`rounded px-2.5 py-1 font-medium transition ${
                            rightView === id
                              ? 'bg-apolo-surface3 text-apolo-textPrimary'
                              : 'text-apolo-textTertiary hover:text-apolo-textSecondary'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* (Pedro: "features repetidas dentro do colapsar saídas")
                          — the Editor/Chat toggle lived BOTH here and in the
                          always-visible header cluster (same setIdeMode). Removed
                          the duplicate; the header one stays canonical. */}
                      <button
                        type="button"
                        onClick={toggleOutputs}
                        aria-label="Colapsar painel de saídas"
                        title="Colapsar saídas"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 motion-reduce:transition-none"
                      >
                        <PanelRightClose size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    {rightView === 'tasks' ? (
                      <div className="min-h-0 flex-1 overflow-hidden">
                        <TasksPanel sessionId={session.id} evalData={keepEval.evalData} />
                      </div>
                    ) : rightView === 'canvas' ? (
                      <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
                        <CanvasPanel sessionId={session.id} />
                      </div>
                    ) : rightView === 'menu' ? (
                      <ChatRightMenu
                        sessionMeta={headerMenuMeta}
                        // (Pedro) decluttered menu, default-hidden behind apolo_show_selfimprove='1':
                        //   • SELF-IMPROVE group + AI › Learning — the self-improve loop is automatic
                        //     (auto-promote replay-gated + useEvaluatorAutoRun + worker crons), no panel needed.
                        //   • ESSENCIAIS group (Files + Terminal) — Files still runs (⇧⌘F + Canvas + IPC);
                        //     Terminal is an Epic-17 placeholder. Both just removed from the menu nav.
                        // Agents stays (dispatch cockpit). Flip the flag to bring every section back.
                        groups={readShowSelfImproveEnabled()
                          ? HEADER_MENU_GROUPS
                          : HEADER_MENU_GROUPS
                            .filter((g) => g.label !== 'Self-improve' && g.label !== 'Essenciais')
                            .map((g) => (g.label === 'AI'
                              ? { ...g, tabs: g.tabs.filter((t) => t.id !== 'learning') }
                              : g))}
                        activePanelId={panelOpen ? panelTab : null}
                        onPanelSelect={handleTabClick}
                        sessionId={session.id}
                        session={session}
                        messages={messages}
                        model={session.model}
                        toolbar={toolbar}
                      />
                    ) : (
                      <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
                        <OutputsRail
                          messages={messages}
                          sessionId={session.id}
                          onOpen={(art) => window.dispatchEvent(new CustomEvent('apolo:openDiff', { detail: { path: art?.path } }))}
                        />
                      </div>
                    )}
                  </div>
                </>
            </aside>
          )}
        </div>
      </div>

      <SidePanel
        open={panelOpen}
        activeTab={panelTab}
        onTabChange={setPanelTab}
        onClose={() => setPanelOpen(false)}
        sessionId={session?.id}
        sessionDirectory={session?.directory}
      />
    </div>
  );
}
